import {
  callableReadKey,
  runAdmittedCallableRead,
  runKeyedAdmittedCallableRead,
} from '@kop/callable-read-admission';
import { getApps, initializeApp } from 'firebase/app';
import { identitySessionFailure } from './identity-session-disposition';
import { requiresHeadquartersOnboarding } from './headquarters-onboarding';
import { getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import {
  collection,
  connectFirestoreEmulator,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore/lite';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import {
  connectStorageEmulator,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import {
  identitySessionExpired,
  identitySessionStartedAt,
  millisecondsUntilNextLocalDay,
  replaceIdentityExpiryTimer,
} from './identity-session.js';
import { confirmedSignedOut } from './identity-auth-observer.js';
import { retryIdentitySessionRead } from './identity-session-read.js';

export { invalidateCallableReadAdmission } from '@kop/callable-read-admission';

export type OperationsEmulatorConfig = {
  projectId: string;
  firestore: { host: string; port: number };
  functions: { host: string; port: number };
  auth?: { host: string; port: number };
  storage?: { host: string; port: number };
};

export type IdentityFirebaseConfig = {
  /**
   * Cloud Storage bucket for the signature object path. It is optional here
   * because the identity app booted without one long before signatures existed,
   * and a missing value must not stop the app from starting; the signature
   * upload reports `SIGNATURE_STORAGE_UNCONFIGURED` instead of guessing a bucket
   * name.
   */
  storageBucket?: string;
  projectId: string;
  apiKey: string;
  appId: string;
  authDomain: string;
  appCheckSiteKey: string;
};

const developmentBuild = (import.meta as unknown as { env: { DEV: boolean } }).env.DEV;

export function identityPasswordReauthenticationError(error: unknown): Error {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';
  const translated =
    code === 'auth/wrong-password' ||
    code === 'auth/invalid-credential' ||
    code === 'auth/invalid-login-credentials'
      ? 'AUTH_REAUTH_WRONG_PASSWORD'
      : code === 'auth/too-many-requests'
        ? 'AUTH_REAUTH_TOO_MANY_REQUESTS'
        : code === 'auth/network-request-failed'
          ? 'AUTH_REAUTH_NETWORK_FAILED'
          : 'AUTH_REAUTH_FAILED';
  return Object.assign(new Error(translated), { code: translated });
}

export async function retryTransientCallableRead<T>(
  read: () => Promise<T>,
  delays?: readonly number[],
): Promise<T> {
  return delays ? runAdmittedCallableRead(read, delays) : runAdmittedCallableRead(read);
}

const workspaceOperationalRead = <Result>(
  functions: ReturnType<typeof getFunctions>,
  operation: string,
  payload: Readonly<Record<string, unknown>>,
) => {
  const identityId = getAuth(functions.app).currentUser?.uid;
  if (!identityId) return Promise.reject(new Error('WORKSPACE_READ_AUTH_SESSION_REQUIRED'));
  return runKeyedAdmittedCallableRead(
    callableReadKey(identityId, operation, payload),
    async () =>
      (
        await httpsCallable<Readonly<Record<string, unknown>>, Result>(
          functions,
          'loadWorkspaceOperationalRead',
          { limitedUseAppCheckTokens: operation === 'msds.open-source' },
        )({ operation, payload })
      ).data,
  );
};

const dedicatedSiteAdministrationRead = <Result>(
  functions: ReturnType<typeof getFunctions>,
  callableName: 'loadSiteOrganizationGovernance' | 'loadWorkLocationRegistry',
  payload: Readonly<{ siteId: string }>,
) => {
  const identityId = getAuth(functions.app).currentUser?.uid;
  if (!identityId) return Promise.reject(new Error('WORKSPACE_READ_AUTH_SESSION_REQUIRED'));
  return runKeyedAdmittedCallableRead(
    callableReadKey(identityId, callableName, payload),
    async () =>
      (
        await httpsCallable<typeof payload, Result>(functions, callableName, {
          limitedUseAppCheckTokens: true,
        })(payload)
      ).data,
  );
};

export type IdentityRegistrationAccountType = 'company' | 'contractor';
export type IdentityRegistrationProfileId =
  | 'company-safety-staff'
  | 'company-general-staff'
  | 'contractor-safety-contact'
  | 'contractor-general-contact';
export type IdentityRegistrationResolution = Readonly<{
  accountType: IdentityRegistrationAccountType;
  requestedProfileId: IdentityRegistrationProfileId;
  profileLabel: string;
  contractorDisplayName: string | null;
  organizationUnitDisplayName: string | null;
  source: 'company-domain' | 'invite' | 'directory';
  canSubmit: true;
}>;
export type IdentityRegistrationOptions = Readonly<{
  accountTypeHint: IdentityRegistrationAccountType | null;
  availableSiteCandidates: readonly Readonly<{ candidateId: string; label: string }>[];
  selectedSiteCandidateId: string | null;
  selectionRequired: boolean;
  profiles: readonly Readonly<{
    id: IdentityRegistrationProfileId;
    label: string;
    accountType: IdentityRegistrationAccountType;
  }>[];
  contractors: readonly Readonly<{ candidateId: string; label: string }>[];
  organizationOptions: readonly Readonly<{
    rootUnitId: string;
    label: string;
    selectionMode: 'direct' | 'child-required';
    children: readonly Readonly<{ unitId: string; label: string }>[];
  }>[];
  complete: boolean;
}>;
export type IdentityRegistrationInput = Readonly<{
  email: string;
  password: string;
  invite?: Readonly<{ invitationId: string; proof: string }> | null;
}>;
export type IdentityRegistrationReceipt = Readonly<{
  intentId: string;
  accountTypeHint: IdentityRegistrationAccountType | null;
}>;
export type IdentityRegistrationCompletionInput = Readonly<{
  preRegistrationIntentId: string;
  /** Opaque candidate issued by the registration-options callable. */
  siteCandidateId?: string | null;
  displayName: string;
  accountType: IdentityRegistrationAccountType;
  requestedProfileId: IdentityRegistrationProfileId;
  organizationCandidate?: Readonly<{
    rootUnitId: string;
    childUnitId?: string | null;
  }> | null;
  /** @deprecated Accepted only for exact `hq`, `eumseong`, or `site-eumseong` migration values. */
  organizationUnitCandidateId?: string | null;
  contractorCandidateId?: string | null;
  phone?: string | null;
  phonePurposeConsent?: boolean;
}>;
export type IdentityRegistrationCompletionReceipt = Readonly<{
  intentId: string;
  requestId: string;
  resolution: IdentityRegistrationResolution;
}>;

export function consumeIdentityRegistrationInvite(
  locationValue: Pick<Location, 'hash' | 'pathname' | 'search'> = window.location,
  historyValue: Pick<History, 'replaceState'> = window.history,
): Readonly<{ invitationId: string; proof: string }> | null {
  const params = new URLSearchParams(locationValue.hash.replace(/^#/u, ''));
  const envelope = params.get('access-invite');
  if (!envelope) return null;
  params.delete('access-invite');
  const remainder = params.toString();
  historyValue.replaceState(
    null,
    '',
    `${locationValue.pathname}${locationValue.search}${remainder ? `#${remainder}` : ''}`,
  );
  const separator = envelope.indexOf('.');
  const invitationId = separator < 0 ? '' : envelope.slice(0, separator);
  const proof = separator < 0 ? '' : envelope.slice(separator + 1);
  if (
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(invitationId) ||
    !/^[A-Za-z0-9_-]{32,512}$/u.test(proof)
  )
    return null;
  return { invitationId, proof };
}
export type MsdsFirebaseAllowedAction =
  | 'upload-source'
  | 'request-analysis'
  | 'retry-failed-analysis'
  | 'review-candidate'
  | 'revise-candidate'
  | 'update-site-usage'
  | 'return-candidate'
  | 'approve-candidate'
  | 'finalize-legal-review'
  | 'create-revision'
  | 'archive-record'
  | 'delete-draft'
  | 'open-source'
  | 'export-official';

export type MsdsFirebaseReceipt = Readonly<{
  receiptId: string;
  commandId: string;
  status: 'succeeded';
  aggregateVersion: number;
  processedAt: string;
  replayed: boolean;
  result: Readonly<Record<string, unknown>>;
  receiptHash: string;
  uploadSession?: Readonly<{
    uploadUrl: string;
    objectPath: string;
    expiresAt: string;
    requiredHeaders: Readonly<Record<string, string>>;
  }>;
}>;

export type MsdsFirebaseWorkspace = Readonly<{
  authoringContractors: readonly Readonly<{ contractorId: string; contractorName: string }>[];
  pendingSources: readonly MsdsPendingSource[];
  records: readonly Readonly<Record<string, unknown>>[];
  candidates: readonly Readonly<Record<string, unknown>>[];
  allowedActions: readonly MsdsFirebaseAllowedAction[];
  totalCount: number;
  nextCursor: string | null;
}>;

function msdsObject(value: unknown, label: string) {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error(`MSDS_${label}_INVALID`);
  return value as Record<string, unknown>;
}

function msdsString(value: unknown, label: string) {
  if (typeof value !== 'string' || !value.trim()) throw new Error(`MSDS_${label}_INVALID`);
  return value;
}

function decodeMsdsReceipt(value: unknown): MsdsFirebaseReceipt {
  const raw = msdsObject(value, 'RECEIPT');
  if (raw.status !== 'succeeded' || !Number.isInteger(raw.aggregateVersion))
    throw new Error('MSDS_RECEIPT_INVALID');
  const base = {
    receiptId: msdsString(raw.receiptId, 'RECEIPT_ID'),
    commandId: msdsString(raw.commandId, 'COMMAND_ID'),
    status: 'succeeded',
    aggregateVersion: Number(raw.aggregateVersion),
    processedAt: msdsString(raw.processedAt, 'PROCESSED_AT'),
    replayed: raw.replayed === true,
    result: msdsObject(raw.result, 'RESULT'),
    receiptHash: msdsString(raw.receiptHash, 'RECEIPT_HASH'),
  } as const;
  if (raw.uploadSession !== undefined) {
    const session = msdsObject(raw.uploadSession, 'UPLOAD_SESSION');
    const headers = msdsObject(session.requiredHeaders, 'UPLOAD_HEADERS');
    return {
      ...base,
      uploadSession: {
        uploadUrl: msdsString(session.uploadUrl, 'UPLOAD_URL'),
        objectPath: msdsString(session.objectPath, 'OBJECT_PATH'),
        expiresAt: msdsString(session.expiresAt, 'UPLOAD_EXPIRY'),
        requiredHeaders: Object.fromEntries(
          Object.entries(headers).map(([key, child]) => [key, msdsString(child, 'UPLOAD_HEADER')]),
        ),
      },
    };
  }
  return base;
}

function decodeMsdsWorkspace(value: unknown): MsdsFirebaseWorkspace {
  const raw = msdsObject(value, 'WORKSPACE');
  const actions = Array.isArray(raw.allowedActions) ? raw.allowedActions : [];
  const allowed = new Set<MsdsFirebaseAllowedAction>([
    'upload-source',
    'request-analysis',
    'retry-failed-analysis',
    'review-candidate',
    'revise-candidate',
    'update-site-usage',
    'return-candidate',
    'approve-candidate',
    'finalize-legal-review',
    'create-revision',
    'archive-record',
    'delete-draft',
    'open-source',
    'export-official',
  ]);
  if (
    !actions.every(
      (action) => typeof action === 'string' && allowed.has(action as MsdsFirebaseAllowedAction),
    )
  )
    throw new Error('MSDS_ALLOWED_ACTIONS_INVALID');
  if (
    !Array.isArray(raw.records) ||
    !Array.isArray(raw.candidates) ||
    !Number.isInteger(raw.totalCount)
  )
    throw new Error('MSDS_WORKSPACE_INVALID');
  const options = raw.authoringContractors === undefined ? [] : raw.authoringContractors;
  if (!Array.isArray(options) || options.length > 30)
    throw new Error('MSDS_AUTHORING_CONTRACTORS_INVALID');
  return {
    pendingSources: decodeMsdsPendingSources(raw.pendingSources),
    authoringContractors: options.map((value) => {
      const option = msdsObject(value, 'AUTHORING_CONTRACTOR');
      return {
        contractorId: msdsString(option.contractorId, 'CONTRACTOR_ID'),
        contractorName: msdsString(option.contractorName, 'CONTRACTOR_NAME'),
      };
    }),
    records: raw.records.map((record) => msdsObject(record, 'RECORD')),
    candidates: raw.candidates.map((candidate) => msdsObject(candidate, 'CANDIDATE')),
    allowedActions: actions as MsdsFirebaseAllowedAction[],
    totalCount: Number(raw.totalCount),
    nextCursor: raw.nextCursor === null ? null : msdsString(raw.nextCursor, 'CURSOR'),
  };
}

export function decodeMsdsSourceStatus(value: unknown, manifestId: string) {
  const raw = msdsObject(value, 'SOURCE_STATUS');
  if (
    raw.manifestId !== manifestId ||
    !Number.isInteger(raw.manifestVersion) ||
    Number(raw.manifestVersion) < 1 ||
    !['quarantine', 'released'].includes(String(raw.sourceStatus)) ||
    !(
      raw.scanStatus === null ||
      [
        'pending',
        'uploaded',
        'quarantine',
        'scanning',
        'clean',
        'infected',
        'invalid-pdf',
        'encrypted',
        'failed',
        'timeout',
      ].includes(String(raw.scanStatus))
    ) ||
    !(
      raw.recoveryState === null ||
      ['provider-evidence-required', 'resumed'].includes(String(raw.recoveryState))
    )
  )
    throw new Error('MSDS_SOURCE_STATUS_INVALID');
  const analysisId = raw.analysisId === null ? null : msdsString(raw.analysisId, 'ANALYSIS_ID');
  const status = raw.status === null ? null : msdsString(raw.status, 'ANALYSIS_STATUS');
  if (
    (analysisId === null) !== (status === null) ||
    (status !== null &&
      !['analysis-queued', 'extracting', 'succeeded', 'needs-review', 'failed'].includes(status))
  )
    throw new Error('MSDS_SOURCE_STATUS_INVALID');
  return {
    manifestId,
    manifestVersion: Number(raw.manifestVersion),
    sourceStatus: raw.sourceStatus as 'quarantine' | 'released',
    scanStatus: raw.scanStatus === null ? null : msdsString(raw.scanStatus, 'SCAN_STATUS'),
    analysisId,
    status,
    version:
      raw.version === null || raw.version === undefined
        ? null
        : Number.isInteger(raw.version) && Number(raw.version) >= 1
          ? Number(raw.version)
          : (() => {
              throw new Error('MSDS_ANALYSIS_VERSION_INVALID');
            })(),
    recoveryState:
      raw.recoveryState === null ? null : msdsString(raw.recoveryState, 'RECOVERY_STATE'),
    errorCode:
      raw.errorCode === null || raw.errorCode === undefined
        ? null
        : msdsString(raw.errorCode, 'ANALYSIS_ERROR_CODE'),
  };
}

export type MsdsPendingSource = ReturnType<typeof decodeMsdsSourceStatus> &
  Readonly<{
    fileName: string;
    contractorId: string;
    committedAt: string;
  }>;

export function decodeMsdsPendingSources(value: unknown): readonly MsdsPendingSource[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 30) throw new Error('MSDS_PENDING_SOURCES_INVALID');
  const ids = new Set<string>();
  return value.map((child) => {
    const raw = msdsObject(child, 'PENDING_SOURCE');
    const manifestId = msdsString(raw.manifestId, 'MANIFEST_ID');
    if (ids.has(manifestId)) throw new Error('MSDS_PENDING_SOURCES_INVALID');
    ids.add(manifestId);
    const progress = decodeMsdsSourceStatus(raw, manifestId);
    const committedAt = msdsString(raw.committedAt, 'COMMITTED_AT');
    if (!Number.isFinite(Date.parse(committedAt)) || progress.status === 'succeeded')
      throw new Error('MSDS_PENDING_SOURCES_INVALID');
    return {
      ...progress,
      fileName: msdsString(raw.fileName, 'FILE_NAME'),
      contractorId: msdsString(raw.contractorId, 'CONTRACTOR_ID'),
      committedAt,
    };
  });
}

/** Production MSDS transport. It reuses the authenticated identity app and exposes callables only. */
export function createMsdsFirebasePort(
  config: IdentityFirebaseConfig,
  contractorId: string | null,
  siteId: string,
) {
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId))
    throw new Error('MSDS_REGISTERED_SITE_REQUIRED');
  const app = identityProductionApp(config);
  const auth = getAuth(app);
  if (!auth.currentUser) throw new Error('MSDS_AUTHENTICATED_FIREBASE_APP_REQUIRED');
  const functions = getFunctions(app, 'asia-northeast3');
  const callableOptions = { limitedUseAppCheckTokens: true } as const;
  const execute = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'executeMsdsCommand',
    callableOptions,
  );
  const exportOfficial = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'exportOfficialMsdsLedger',
    callableOptions,
  );
  const loadWorkspace = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'loadMsdsWorkspace',
    callableOptions,
  );
  const loadAnalysisStatus = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'loadMsdsAnalysisStatus',
    callableOptions,
  );
  const loadAnalysisResult = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'loadMsdsAnalysisResult',
    callableOptions,
  );
  const openSourceDocument = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'openMsdsSourceDocument',
    callableOptions,
  );
  return {
    async loadWorkspace(input: Readonly<{ pageSize: number; cursor: string | null }>) {
      return decodeMsdsWorkspace((await loadWorkspace({ siteId, contractorId, ...input })).data);
    },
    async execute(command: Readonly<Record<string, unknown>>) {
      return decodeMsdsReceipt((await execute(command)).data);
    },
    async loadAnalysisStatus(analysisId: string) {
      const raw = msdsObject(
        (await loadAnalysisStatus({ siteId, analysisId, contractorId })).data,
        'ANALYSIS_STATUS',
      );
      return msdsString(raw.status, 'ANALYSIS_STATUS');
    },
    async loadSourceStatus(manifestId: string) {
      return decodeMsdsSourceStatus(
        (await loadAnalysisStatus({ siteId, manifestId, contractorId })).data,
        manifestId,
      );
    },
    async loadAnalysisResult(analysisId: string) {
      return msdsObject(
        (await loadAnalysisResult({ siteId, analysisId, contractorId })).data,
        'ANALYSIS_RESULT',
      );
    },
    async exportOfficial(recordIds: readonly string[], idempotencyKey: string) {
      const raw = msdsObject(
        (await exportOfficial({ siteId, recordIds, idempotencyKey })).data,
        'EXPORT',
      );
      return {
        csv: msdsString(raw.csv, 'EXPORT_CSV'),
        receiptId: msdsString(raw.receiptId, 'EXPORT_RECEIPT_ID'),
        receiptHash: msdsString(raw.receiptHash, 'EXPORT_RECEIPT_HASH'),
        replayed: raw.replayed === true,
      };
    },
    async openSource(manifestId: string) {
      const raw = msdsObject(
        (await openSourceDocument({ siteId, manifestId, contractorId })).data,
        'SOURCE_DOCUMENT',
      );
      const response = await fetch(msdsString(raw.downloadUrl, 'SOURCE_URL'), {
        method: 'GET',
        credentials: 'omit',
      });
      if (!response.ok) throw new Error(`MSDS_SOURCE_DOWNLOAD_FAILED:${response.status}`);
      return response.blob();
    },
    async upload(session: NonNullable<MsdsFirebaseReceipt['uploadSession']>, file: File) {
      const response = await fetch(session.uploadUrl, {
        method: 'PUT',
        headers: session.requiredHeaders,
        body: file,
      });
      if (!response.ok) throw new Error(`MSDS_UPLOAD_FAILED:${response.status}`);
    },
  };
}

export type AccountSettingsFirebaseConfig = IdentityFirebaseConfig & {
  emulator?: {
    firestore: { host: string; port: number };
    functions: { host: string; port: number };
  };
};

export type OperationsCommand = {
  domain: 'heat-index';
  operation: string;
  aggregateId: string;
  expectedVersion: number;
  payload: Readonly<Record<string, unknown>>;
};

async function refreshVerifiedIdentity(user: User) {
  await user.reload();
  if (!user.emailVerified || !user.email)
    throw new Error('Verify the signup email before requesting administrator authorization');
}

async function createRegistrationIntent(
  user: User,
  functions: ReturnType<typeof getFunctions>,
  input: IdentityRegistrationInput,
): Promise<IdentityRegistrationReceipt> {
  const intentId = `intent-${crypto.randomUUID()}`;
  const response = await httpsCallable(functions, 'createIdentityRegistrationIntent', {
    limitedUseAppCheckTokens: true,
  })({
    schemaVersion: 1,
    intentId,
    invite: input.invite ?? null,
  });
  if (user.uid.length === 0) throw new Error('IDENTITY_REGISTRATION_AUTH_REQUIRED');
  const value = response.data as Record<string, unknown>;
  return {
    intentId: String(value.intentId ?? ''),
    accountTypeHint:
      value.accountTypeHint === 'company' || value.accountTypeHint === 'contractor'
        ? value.accountTypeHint
        : null,
  };
}

async function completeIdentityRegistration(
  functions: ReturnType<typeof getFunctions>,
  input: IdentityRegistrationCompletionInput,
): Promise<IdentityRegistrationCompletionReceipt> {
  const completionId = `completion-${crypto.randomUUID()}`;
  return (
    await httpsCallable<Record<string, unknown>, IdentityRegistrationCompletionReceipt>(
      functions,
      'completeIdentityRegistration',
      { limitedUseAppCheckTokens: true },
    )({
      schemaVersion: 1,
      completionId,
      preRegistrationIntentId: input.preRegistrationIntentId,
      siteCandidateId: input.siteCandidateId ?? null,
      displayName: input.displayName,
      accountType: input.accountType,
      requestedProfileId: input.requestedProfileId,
      organizationCandidate: input.organizationCandidate ?? null,
      organizationUnitCandidateId: input.organizationUnitCandidateId ?? null,
      contractorCandidateId: input.contractorCandidateId ?? null,
      phone: input.phone?.trim() || null,
      phonePurposeConsent: input.phonePurposeConsent === true,
    })
  ).data;
}

export async function settleIdentityRegistration<T>(
  complete: () => Promise<T>,
  updateDisplayName: () => Promise<unknown>,
  refreshSession: () => Promise<unknown>,
) {
  const receipt = await complete();
  await updateDisplayName().catch(() => undefined);
  await refreshSession().catch(() => undefined);
  return receipt;
}

function canonicalIdentityPersona(value: unknown) {
  const persona = String(value ?? '');
  if (
    ![
      'system-admin',
      'hq-safety-admin',
      'hq-staff',
      'hq-auditor',
      'site-manager',
      'site-staff',
      'safety-manager',
      'safety-staff',
      'partner-admin',
    ].includes(persona)
  )
    throw new Error('CANONICAL_IDENTITY_PERSONA_REQUIRED');
  return persona;
}

const CANONICAL_AUTHORITY_SITE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;

function canonicalSessionSiteResource(
  scopeId: string,
  role: string,
  resource: Record<string, unknown>,
) {
  if (resource.kind === 'tenant')
    return role === 'system' || role === 'hqSafety' || role === 'hqAudit';
  const siteId = typeof resource.siteId === 'string' ? resource.siteId : '';
  if (!CANONICAL_AUTHORITY_SITE_ID.test(siteId) || !scopeId.startsWith(`${siteId}-`)) return false;
  if (resource.kind === 'site') return role !== 'partner' && role !== 'system';
  return (
    resource.kind === 'contractor' &&
    role === 'partner' &&
    typeof resource.contractorId === 'string' &&
    CANONICAL_AUTHORITY_SITE_ID.test(resource.contractorId)
  );
}

export function canonicalIdentitySessionAuthority(session: Record<string, unknown>) {
  if (!Array.isArray(session.scopes) || session.scopes.length === 0)
    throw new Error('CANONICAL_IDENTITY_SCOPES_REQUIRED');
  const decodedScopes = session.scopes.map((value) => {
    if (!value || typeof value !== 'object') throw new Error('CANONICAL_IDENTITY_SCOPE_REQUIRED');
    const scope = value as Record<string, unknown>;
    const id = String(scope.id ?? '');
    const role = String(scope.role ?? '');
    const persona = canonicalIdentityPersona(scope.persona);
    const resource = scope.resource as Record<string, unknown> | undefined;
    if (
      !id ||
      !['client', 'partner', 'siteLead', 'hqSafety', 'hqAudit', 'system'].includes(role) ||
      !resource ||
      resource.tenantId !== 'kop' ||
      !['tenant', 'site', 'contractor'].includes(String(resource.kind ?? '')) ||
      !Array.isArray(scope.capabilities) ||
      scope.capabilities.some((capability) => typeof capability !== 'string')
    )
      throw new Error('CANONICAL_IDENTITY_SCOPE_REQUIRED');
    if (!canonicalSessionSiteResource(id, role, resource))
      throw new Error('CANONICAL_IDENTITY_SCOPE_RESOURCE_REQUIRED');
    // The authenticated server resolves the public ID from the verified site master.
    // Keep authority scope IDs stable; never infer a public ID from their spelling.
    const publicSiteId = scope.publicSiteId;
    if (
      publicSiteId !== undefined &&
      (resource.kind === 'tenant' ||
        typeof publicSiteId !== 'string' ||
        !CANONICAL_AUTHORITY_SITE_ID.test(publicSiteId))
    )
      throw new Error('CANONICAL_IDENTITY_PUBLIC_SITE_REQUIRED');
    const businessRole = [
      'hq-staff',
      'hq-safety-manager',
      'site-staff',
      'site-safety-manager',
      'contractor-staff',
    ].find((candidate) => candidate === scope.businessRole);
    return {
      id,
      label: String(scope.label ?? ''),
      detail: String(scope.detail ?? ''),
      role,
      persona,
      resource: publicSiteId === undefined ? resource : { ...resource, siteId: publicSiteId },
      capabilities: [...scope.capabilities],
      // Optional feature policy is scoped by the authenticated server. Preserve even an
      // invalid value for the feature boundary to diagnose; it must never reject login.
      ...(Object.hasOwn(scope, 'featureAccess') ? { featureAccess: scope.featureAccess } : {}),
      ...(scope.developerReadOnly === true ? { developerReadOnly: true as const } : {}),
      ...(businessRole !== undefined || scope.businessRole === null
        ? { businessRole: businessRole ?? null }
        : {}),
      ...(typeof scope.developer === 'boolean' ? { developer: scope.developer } : {}),
    };
  });
  if (new Set(decodedScopes.map((scope) => scope.id)).size !== decodedScopes.length)
    throw new Error('CANONICAL_IDENTITY_SCOPE_IDS_REQUIRED');
  const scopes = decodedScopes.filter(
    (scope) =>
      (scope.role !== 'system' && scope.persona !== 'system-admin') ||
      (scope.id === 'kop-system' &&
        scope.role === 'system' &&
        scope.persona === 'system-admin' &&
        scope.resource.kind === 'tenant'),
  );
  if (scopes.length === 0) throw new Error('CANONICAL_IDENTITY_SCOPES_REQUIRED');
  const defaultActiveScopeId = String(session.defaultActiveScopeId ?? '');
  const visibleDefaultScopeId = scopes.some((scope) => scope.id === defaultActiveScopeId)
    ? defaultActiveScopeId
    : scopes[0]!.id;
  return { scopes, defaultActiveScopeId: visibleDefaultScopeId };
}

async function loadIdentityRegistrationOptions(
  functions: ReturnType<typeof getFunctions>,
  email: string,
  siteCandidateId?: string | null,
): Promise<IdentityRegistrationOptions> {
  const load = httpsCallable<
    { email: string; siteCandidateId?: string | null },
    IdentityRegistrationOptions
  >(functions, 'loadIdentityRegistrationOptions', { limitedUseAppCheckTokens: true });
  return retryTransientCallableRead(
    async () =>
      (
        await load({
          email: email.trim().toLowerCase(),
          ...(siteCandidateId ? { siteCandidateId } : {}),
        })
      ).data,
    // Registration completion is a one-time read. Give a quota-bound cold
    // instance enough time to become ready without ever replaying a write.
    [800, 1_800, 3_500, 6_000],
  );
}

export type WorkControlPatrolCommand = Readonly<{
  ownerUid: string;
  tenantId: 'kop';
  commandId: string;
  idempotencyKey: string;
  caseId: string;
  clientEventId: string;
  expectedVersion: number;
  scope: Readonly<{
    kind: 'contractor';
    tenantId: 'kop';
    siteId: 'site-eumseong';
    contractorId: string;
  }>;
  patrol: Readonly<{
    result: 'conforming' | 'corrected-on-site' | 'work-stopped';
    note: string;
    capturedAt: string;
    evidenceManifestId: string;
    evidence: Readonly<{ manifestId: string; sha256: string }> | null;
  }>;
  queuedAt: string;
}>;

export type WorkControlPatrolReceipt = Readonly<{
  commandId: string;
  idempotencyKey: string;
  status: 'succeeded' | 'rejected';
  aggregateVersion: number | null;
  processedAt: string;
  replayed: boolean;
  acknowledgedEventIds: string[];
  errorCode?: string | null;
  message?: string | null;
}>;

export type WorkControlAuthorityCommand = Readonly<{
  tenantId: 'kop';
  commandId: string;
  idempotencyKey: string;
  caseId: string;
  expectedVersion: number;
  scope: WorkControlPatrolCommand['scope'];
  operation:
    | 'case.stop'
    | 'case.resume'
    | 'case.change-workers'
    | 'case.record-induction'
    | 'case.record-tbm'
    | 'case.activate';
  payload: Readonly<Record<string, unknown>>;
}>;

export type WorkControlAuthorityReceipt = Omit<WorkControlPatrolReceipt, 'acknowledgedEventIds'>;

export type WorkControlV3Scope =
  | Readonly<{ kind: 'site'; tenantId: 'kop'; siteId: string }>
  | Readonly<{
      kind: 'contractor';
      tenantId: 'kop';
      siteId: string;
      contractorId: string;
    }>;

export type WorkControlV3MutationReceipt = Readonly<{
  receiptId: string;
  workId: string;
  aggregateVersion?: number;
  deletedVersion?: number;
  processedAt?: string;
  deletedAt?: string;
  replayed: boolean;
}>;

export type MigrationRehearsalImport = Readonly<{
  packetId: string;
  packetRevision: number;
  sourceAllowlistHash: string;
  bundle: Readonly<Record<string, unknown>>;
}>;

export function createWorkControlPatrolEmulatorPort(config: OperationsEmulatorConfig) {
  if (!developmentBuild) throw new Error('WORK_CONTROL_PATROL_LOOPBACK_ONLY');
  if (
    !config.projectId.startsWith('demo-') ||
    !loopback(config.firestore.host) ||
    !loopback(config.functions.host)
  ) {
    throw new Error('WORK_CONTROL_PATROL_LOOPBACK_ONLY');
  }
  const app = getApps().find(
    (candidate) =>
      candidate.options.projectId === config.projectId && getAuth(candidate).currentUser,
  );
  if (!app) throw new Error('SHARED_AUTHENTICATED_FIREBASE_APP_REQUIRED');
  const database = getFirestore(app);
  const functions = getFunctions(app, 'asia-northeast3');
  connectFirestoreEmulator(database, config.firestore.host, config.firestore.port);
  connectFunctionsEmulator(functions, config.functions.host, config.functions.port);
  return {
    async send(command: WorkControlPatrolCommand): Promise<WorkControlPatrolReceipt> {
      if (
        command.tenantId !== 'kop' ||
        command.scope.siteId !== 'site-eumseong' ||
        command.scope.kind !== 'contractor'
      ) {
        throw new Error('EUMSEONG_CONTRACTOR_SCOPE_REQUIRED');
      }
      return (
        await httpsCallable<WorkControlPatrolCommand, WorkControlPatrolReceipt>(
          functions,
          'submitWorkControlPatrol',
        )(command)
      ).data;
    },
    async sendCommand(command: WorkControlAuthorityCommand): Promise<WorkControlAuthorityReceipt> {
      if (
        command.tenantId !== 'kop' ||
        command.scope.siteId !== 'site-eumseong' ||
        command.scope.kind !== 'contractor'
      ) {
        throw new Error('EUMSEONG_CONTRACTOR_SCOPE_REQUIRED');
      }
      return (
        await httpsCallable<WorkControlAuthorityCommand, WorkControlAuthorityReceipt>(
          functions,
          'submitWorkControlCommand',
        )(command)
      ).data;
    },
    async getCaseContext(tenantId: 'kop', caseId: string) {
      const snapshot = await getDoc(doc(database, 'tenants', tenantId, 'workControlCases', caseId));
      if (!snapshot.exists()) return null;
      const value = snapshot.data();
      const scope = value.scope as Record<string, unknown> | undefined;
      if (
        value.caseId !== caseId ||
        !['active', 'suspended', 'stopped'].includes(String(value.status)) ||
        scope?.kind !== 'contractor' ||
        scope.tenantId !== 'kop' ||
        scope.siteId !== 'site-eumseong' ||
        typeof scope.contractorId !== 'string' ||
        !scope.contractorId ||
        !Number.isInteger(value.version) ||
        Number(value.version) < 1
      ) {
        throw new Error('CANONICAL_ACTIVE_WORK_CONTROL_CASE_REQUIRED');
      }
      return {
        caseId,
        version: Number(value.version),
        scope: {
          kind: 'contractor' as const,
          tenantId: 'kop' as const,
          siteId: 'site-eumseong' as const,
          contractorId: String(scope.contractorId),
        },
      };
    },
    async getWorkControlCase(tenantId: 'kop', caseId: string) {
      const snapshot = await getDoc(doc(database, 'tenants', tenantId, 'workControlCases', caseId));
      if (!snapshot.exists()) return null;
      const value = snapshot.data();
      const scope = value.scope as Record<string, unknown> | undefined;
      if (
        value.caseId !== caseId ||
        scope?.tenantId !== 'kop' ||
        scope.siteId !== 'site-eumseong' ||
        scope.kind !== 'contractor' ||
        typeof scope.contractorId !== 'string' ||
        !Number.isInteger(value.version)
      ) {
        throw new Error('CANONICAL_WORK_CONTROL_CASE_REQUIRED');
      }
      return value;
    },
    async getClosedWorkControlCase(tenantId: 'kop', caseId: string) {
      const snapshot = await getDoc(doc(database, 'tenants', tenantId, 'workControlCases', caseId));
      if (!snapshot.exists()) return null;
      const value = snapshot.data();
      const scope = value.scope as Record<string, unknown> | undefined;
      const lock = value.integrityLock as Record<string, unknown> | undefined;
      if (
        value.caseId !== caseId ||
        scope?.kind !== 'contractor' ||
        scope.tenantId !== 'kop' ||
        scope.siteId !== 'site-eumseong' ||
        typeof scope.contractorId !== 'string'
      ) {
        throw new Error('CANONICAL_CLOSED_WORK_CONTROL_CASE_REQUIRED');
      }
      if (value.status !== 'closed') return null;
      if (!/^[a-f0-9]{64}$/u.test(String(lock?.contentHash ?? '')))
        throw new Error('CANONICAL_CLOSED_WORK_CONTROL_CASE_REQUIRED');
      return value;
    },
    async getWorkControlEvidencePackage(tenantId: 'kop', caseId: string, packageHash: string) {
      if (!/^[a-f0-9]{64}$/u.test(packageHash)) throw new Error('CANONICAL_PACKAGE_HASH_REQUIRED');
      const snapshot = await getDoc(
        doc(
          database,
          'tenants',
          tenantId,
          'workControlCases',
          caseId,
          'evidencePackages',
          packageHash,
        ),
      );
      if (!snapshot.exists()) return null;
      const value = snapshot.data();
      const scope = value.scope as Record<string, unknown> | undefined;
      if (
        value.caseId !== caseId ||
        value.packageHash !== packageHash ||
        scope?.kind !== 'contractor' ||
        scope.tenantId !== 'kop' ||
        scope.siteId !== 'site-eumseong'
      ) {
        throw new Error('CANONICAL_EVIDENCE_PACKAGE_REQUIRED');
      }
      return value;
    },
  };
}

function workControlV3CallablePort(functions: ReturnType<typeof getFunctions>) {
  const actorUid = getAuth(functions.app).currentUser?.uid;
  const withActor = async <Result>(run: () => Promise<Result>) => {
    const assertActor = () => {
      if (!actorUid || getAuth(functions.app).currentUser?.uid !== actorUid)
        throw new Error('WORK_CONTROL_STALE_SESSION');
    };
    assertActor();
    const value = await run();
    assertActor();
    return value;
  };
  const call = <Result>(name: string, payload: unknown) =>
    withActor(
      async () =>
        (
          await httpsCallable<unknown, Result>(functions, name, { limitedUseAppCheckTokens: true })(
            payload,
          )
        ).data,
    );
  return {
    reserveAuthoringContext(input: { scope: WorkControlV3Scope; idempotencyKey: string }) {
      return call<Readonly<Record<string, unknown>>>('reserveWorkControlAuthoringContextV3', input);
    },
    list(input: {
      scope: WorkControlV3Scope;
      pageSize?: number;
      cursor?: Readonly<Record<string, unknown>> | null;
    }) {
      return withActor(() =>
        httpsCallable<unknown, Readonly<Record<string, unknown>>>(
          functions,
          'loadWorkControlLedgerV3',
        )(input).then((result) => result.data),
      );
    },
    detail(scope: WorkControlV3Scope, workId: string) {
      return withActor(() =>
        httpsCallable<unknown, Readonly<Record<string, unknown>>>(
          functions,
          'loadWorkControlDetailV3',
        )({ scope, workId }).then((result) => result.data),
      );
    },
    save(input: {
      aggregate: Readonly<Record<string, unknown>>;
      expectedVersion: number;
      idempotencyKey: string;
      changeReason?: string;
      authoringContextId?: string;
    }) {
      return call<WorkControlV3MutationReceipt>('saveWorkControlV3', input);
    },
    submitForApproval(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('submitWorkControlForApprovalV3', input);
    },
    issuePermit(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('issueWorkControlPermitV3', input);
    },
    startWork(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('startWorkControlV3', input);
    },
    recordPatrol(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('recordWorkControlPatrolV3', input);
    },
    resolvePatrolCorrection(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('resolveWorkControlPatrolCorrectionV3', input);
    },
    verifyChecklistCorrection(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('verifyWorkControlChecklistCorrectionV3', input);
    },
    confirmCloseout(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('confirmWorkControlCloseoutV3', input);
    },
    reviewEquipmentPlan(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('reviewWorkControlEquipmentPlanV3', input);
    },
    preparePublication(input: Readonly<Record<string, unknown>>) {
      return call<Readonly<Record<string, unknown>>>('prepareWorkControlPublicationV3', input);
    },
    publish(input: Readonly<Record<string, unknown>>) {
      return call<WorkControlV3MutationReceipt>('recordWorkControlTbmV3', input);
    },
    loadMyPendingSignatures(input: Readonly<Record<string, unknown>> = {}) {
      return withActor(() =>
        workspaceOperationalRead<Readonly<Record<string, unknown>>>(
          functions,
          'work-control.pending-signatures',
          input,
        ),
      );
    },
    loadMyInfo(scope: WorkControlV3Scope) {
      return call<Readonly<Record<string, unknown>>>('loadMyInfo', {
        siteId: scope.siteId,
        targetIdentityId: actorUid ?? '',
      });
    },
    bindPersonalSignatureUse(
      scope: WorkControlV3Scope,
      binding: Readonly<Record<string, unknown>>,
    ) {
      return call<Readonly<Record<string, unknown>>>('bindPersonalSignatureUse', {
        siteId: scope.siteId,
        binding,
      });
    },
    hardDelete(input: { workId: string; expectedVersion: number; idempotencyKey: string }) {
      return call<WorkControlV3MutationReceipt>('hardDeleteWorkControlV3', input);
    },
    renderBundle(input: { workId: string; expectedVersion: number; idempotencyKey: string }) {
      return call<Readonly<Record<string, unknown>>>('renderWorkControlBundleV3', input);
    },
    manageDay(input: Readonly<Record<string, unknown>>) {
      if (input.action === 'load' || input.action === 'document') {
        return withActor(() => httpsCallable<unknown, Readonly<Record<string, unknown>>>(
          functions, 'loadWorkControlDayV3',
        )(input).then(result => result.data));
      }
      return call<Readonly<Record<string, unknown>>>('manageWorkControlDayV3', input);
    },
  };
}

export function createWorkControlV3EmulatorPort(config: OperationsEmulatorConfig) {
  if (!developmentBuild) throw new Error('WORK_CONTROL_V3_LOOPBACK_ONLY');
  if (
    !config.projectId.startsWith('demo-') ||
    !loopback(config.firestore.host) ||
    !loopback(config.functions.host)
  )
    throw new Error('WORK_CONTROL_V3_LOOPBACK_ONLY');
  const app = getApps().find(
    (candidate) =>
      candidate.options.projectId === config.projectId && getAuth(candidate).currentUser,
  );
  if (!app) throw new Error('SHARED_AUTHENTICATED_FIREBASE_APP_REQUIRED');
  const functions = getFunctions(app, 'asia-northeast3');
  connectFunctionsEmulator(functions, config.functions.host, config.functions.port);
  return workControlV3CallablePort(functions);
}

export function createWorkControlV3FirebasePort(config: IdentityFirebaseConfig) {
  return workControlV3CallablePort(getFunctions(identityProductionApp(config), 'asia-northeast3'));
}

const loopback = (host: string) => ['127.0.0.1', 'localhost', '::1'].includes(host);

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, stableValue(child)]),
    );
  return value;
}

async function sha256(value: unknown) {
  const bytes = new TextEncoder().encode(JSON.stringify(stableValue(value)));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export type CursorPage<T, Cursor> = {
  records: readonly T[];
  nextCursor: Cursor | null;
  exhausted: boolean;
};

export type CursorCollectionResult<T> = {
  records: readonly T[];
  pagesRead: number;
  cursorExhausted: true;
};

/** Read until the repository explicitly proves cursor exhaustion. */
export async function collectCursorPages<T, Cursor>(
  loadPage: (cursor: Cursor | null) => Promise<CursorPage<T, Cursor>>,
): Promise<CursorCollectionResult<T>> {
  const records: T[] = [];
  let cursor: Cursor | null = null;
  let pagesRead = 0;
  for (;;) {
    const page = await loadPage(cursor);
    pagesRead += 1;
    records.push(...page.records);
    if (page.exhausted) return { records, pagesRead, cursorExhausted: true };
    if (
      page.nextCursor === null ||
      page.records.length === 0 ||
      Object.is(page.nextCursor, cursor)
    ) {
      throw new Error('Projection cursor did not advance before exhaustion');
    }
    cursor = page.nextCursor;
  }
}

function identityAdminCallableRepository(functions: ReturnType<typeof getFunctions>) {
  const load = async (input: Readonly<Record<string, unknown>> = {}) => {
    const data = (
      await httpsCallable(functions, 'loadIdentityAdminWorkspace', {
        limitedUseAppCheckTokens: true,
      })(input)
    ).data as {
      users?: Array<Record<string, unknown>>;
      contractors?: Array<Record<string, unknown>>;
      sites?: Array<Record<string, unknown>>;
      selectedSiteId?: string;
      selectedSiteLabel?: string;
      canAppointSafetyManager?: boolean;
      currentSafetyManager?: Record<string, unknown> | null;
      safetyManagerCount?: number;
      complete?: boolean;
    };
    return {
      complete: Boolean(data.complete),
      completeness: data.complete ? ('complete' as const) : ('bounded' as const),
      total: Number((data as Record<string, unknown>).total ?? 0),
      nextCursor:
        typeof (data as Record<string, unknown>).nextCursor === 'string'
          ? String((data as Record<string, unknown>).nextCursor)
          : null,
      source: 'server-read' as const,
      sites: (data.sites ?? []).map((site) => ({
        siteId: String(site.siteId ?? ''),
        label: String(site.label ?? ''),
      })),
      selectedSiteId: String(data.selectedSiteId ?? input.siteId ?? 'site-eumseong'),
      selectedSiteLabel: String(data.selectedSiteLabel ?? ''),
      canAppointSafetyManager: data.canAppointSafetyManager === true,
      currentSafetyManager:
        data.currentSafetyManager && typeof data.currentSafetyManager === 'object'
          ? {
              displayName: String(data.currentSafetyManager.displayName ?? ''),
              email: String(data.currentSafetyManager.email ?? ''),
            }
          : null,
      safetyManagerCount: Number(data.safetyManagerCount ?? 0),
      contractors: (data.contractors ?? []).map((value) => ({
        id: String(value.contractorId ?? ''),
        name: String(value.legalName ?? ''),
        engagementEndsOn: String(value.engagementEndsOn ?? ''),
        active: value.active === true,
      })),
      users: (data.users ?? [])
        .filter((value) => {
          if (value.status === 'historical') return true;
          const membership = (value.membership ?? {}) as Record<string, unknown>;
          const site = (value.siteMembership ?? {}) as Record<string, unknown>;
          const tenantPersonas = Array.isArray(membership.personas) ? membership.personas : [];
          const sitePersonas = Array.isArray(site.personas) ? site.personas : [];
          const visibleSiteAssignment =
            site.role !== 'system' &&
            sitePersonas.length > 0 &&
            !sitePersonas.includes('system-admin');
          const visibleTenantAssignment =
            membership.role !== 'system' && !tenantPersonas.includes('system-admin');
          return visibleSiteAssignment || visibleTenantAssignment;
        })
        .map((value) => {
          const membership = (value.membership ?? {}) as Record<string, unknown>;
          const site = (value.siteMembership ?? {}) as Record<string, unknown>;
          const accessRequest =
            value.accessRequest && typeof value.accessRequest === 'object'
              ? (value.accessRequest as Record<string, unknown>)
              : null;
          const resolution =
            accessRequest?.resolution && typeof accessRequest.resolution === 'object'
              ? (accessRequest.resolution as Record<string, unknown>)
              : null;
          return {
            uid: String(value.identityId ?? ''),
            displayName: String(value.displayName ?? ''),
            email: String(value.email ?? ''),
            phone: value.phone == null ? null : String(value.phone),
            jobTitle: value.jobTitle == null ? null : String(value.jobTitle),
            jobGrade: value.jobGrade == null ? null : String(value.jobGrade),
            status: String(value.status ?? 'pending'),
            role: site.role ?? membership.role ?? null,
            personas: [
              ...new Set([
                ...(Array.isArray(site.personas)
                  ? site.personas.filter((value): value is string => typeof value === 'string')
                  : []),
                ...(Array.isArray(membership.personas)
                  ? membership.personas.filter(
                      (value): value is string => typeof value === 'string',
                    )
                  : []),
              ]),
            ],
            siteId: site.siteId ?? null,
            contractorId: site.contractorId ?? null,
            version: Number(value.version ?? 0),
            requestedAt: String(value.createdAt ?? ''),
            validUntil: membership.validUntil ?? null,
            reason: value.suspensionReason ?? null,
            accessRequest:
              accessRequest && resolution
                ? {
                    requestId: String(accessRequest.requestId ?? ''),
                    requestedProfileId: String(accessRequest.requestedProfileId ?? ''),
                    profileLabel: String(resolution.profileLabel ?? ''),
                    organizationUnitDisplayName:
                      resolution.organizationUnitDisplayName == null
                        ? null
                        : String(resolution.organizationUnitDisplayName),
                    contractorDisplayName:
                      resolution.contractorDisplayName == null
                        ? null
                        : String(resolution.contractorDisplayName),
                    source: String(resolution.source ?? ''),
                    status: String(accessRequest.status ?? 'pending'),
                  }
                : null,
            profile:
              value.profile && typeof value.profile === 'object'
                ? value.profile
                : {
                    id: '',
                    label: '미배정',
                    organization: String(data.selectedSiteLabel ?? '사업장'),
                    contractor: null,
                  },
            allowedActions: Array.isArray(value.allowedActions) ? value.allowedActions : [],
            assignmentCandidates: Array.isArray(value.assignmentCandidates)
              ? value.assignmentCandidates
              : [],
          };
        }),
    };
  };
  const execute = async (
    operation: string,
    uid: string,
    version: number,
    payload: Record<string, unknown>,
    query: Readonly<Record<string, unknown>> = {},
  ) => {
    const seed = crypto.randomUUID();
    const publicSiteId =
      typeof query.siteId === 'string' && query.siteId.length > 0 ? query.siteId : 'site-eumseong';
    await httpsCallable(functions, 'executeIdentityCommand', { limitedUseAppCheckTokens: true })({
      commandId: `identity-${seed}`,
      idempotencyKey: `identity-${seed}`,
      tenantId: 'kop',
      siteId: publicSiteId,
      targetUid: uid,
      expectedVersion: version,
      operation,
      payload,
    });
    return load(query);
  };
  return {
    load,
    approve: (
      uid: string,
      version: number,
      role: string,
      contractorId: string | null,
      query = {},
    ) => execute('identity.approve', uid, version, { role, contractorId }, query),
    approveRequestedBundle: (uid: string, version: number, requestId: string, query = {}) =>
      execute('identity.approve-requested-bundle', uid, version, { requestId }, query),
    rejectRequestedBundle: (
      uid: string,
      version: number,
      requestId: string,
      reason: string,
      query = {},
    ) => execute('identity.reject-requested-bundle', uid, version, { requestId, reason }, query),
    depart: (uid: string, version: number, reason: string, query = {}) =>
      execute('identity.depart', uid, version, { reason }, query),
    correctAssignment: (
      uid: string,
      version: number,
      candidateId: string,
      reason: string,
      query = {},
    ) => execute('identity.correct-assignment', uid, version, { candidateId, reason }, query),
    appointSafetyManager: (
      uid: string,
      version: number,
      siteId: string,
      query: Readonly<Record<string, unknown>> = {},
    ) => execute('identity.appoint-safety-manager', uid, version, {}, { ...query, siteId }),
    suspend: (uid: string, version: number, reason: string) =>
      execute('identity.suspend', uid, version, { reason }),
    changeScope: (uid: string, version: number, role: string, contractorId: string | null) =>
      execute('identity.change-scope', uid, version, { role, contractorId }),
  };
}

function accountSettingsCallableRepository(functions: ReturnType<typeof getFunctions>) {
  const load = async (siteId = 'site-eumseong') =>
    (
      await httpsCallable(
        functions,
        'loadAccountSettingsWorkspace',
      )({
        scopeKind: 'site',
        projection: 'site-basics',
        siteId,
      })
    ).data;
  const loadSiteMasters = async () =>
    (
      await httpsCallable(
        functions,
        'loadAccountSettingsWorkspace',
      )({
        scopeKind: 'tenant',
        projection: 'site-masters',
      })
    ).data;
  return {
    load,
    async loadSite(siteId: string) {
      return load(siteId);
    },
    loadSiteMasters,
    async execute(
      input: {
        operation: string;
        aggregateId: string;
        expectedVersion: number;
        payload: Readonly<Record<string, unknown>>;
      },
      publicSiteId = 'site-eumseong',
    ) {
      const seed = crypto.randomUUID();
      await httpsCallable(
        functions,
        'executeAccountSettingsCommand',
      )({
        commandId: `account-settings-${seed}`,
        idempotencyKey: `account-settings-${seed}`,
        tenantId: 'kop',
        siteId:
          input.operation === 'site.register' || input.operation === 'site-master.update'
            ? 'eumseong'
            : publicSiteId,
        ...input,
      });
      return load(publicSiteId);
    },
  };
}

/** Production endpoints are intentionally unaddressable from this adapter. */
export function createOperationsFirebaseEmulatorAdapter(config: OperationsEmulatorConfig) {
  if (!developmentBuild) throw new Error('OPERATIONS_FIREBASE_EMULATOR_ONLY');
  if (
    !config.projectId.startsWith('demo-') ||
    !loopback(config.firestore.host) ||
    !loopback(config.functions.host) ||
    (config.storage !== undefined && !loopback(config.storage.host))
  )
    throw new Error(
      'Operations Firebase adapter accepts demo-* projects and loopback emulators only',
    );
  const name = 'operations-resource-emulator';
  const app =
    getApps().find(
      (candidate) =>
        candidate.options.projectId === config.projectId && getAuth(candidate).currentUser,
    ) ??
    getApps().find((candidate) => candidate.name === name) ??
    initializeApp(
      {
        projectId: config.projectId,
        apiKey: 'demo-emulator-only',
        appId: 'demo-operations-resource',
        storageBucket: `${config.projectId}.appspot.com`,
      },
      name,
    );
  const firestore = getFirestore(app);
  const functions = getFunctions(app, 'asia-northeast3');
  connectFirestoreEmulator(firestore, config.firestore.host, config.firestore.port);
  connectFunctionsEmulator(functions, config.functions.host, config.functions.port);
  if (config.storage) {
    connectStorageEmulator(getStorage(app), config.storage.host, config.storage.port);
  }
  return {
    async runtimeHealthCheck() {
      return (await httpsCallable(functions, 'runtimeHealthCheck')({})).data;
    },
    identityAdminRepository() {
      return identityAdminCallableRepository(functions);
    },
    accountSettingsRepository() {
      return accountSettingsCallableRepository(functions);
    },
    migrationAuthorityRepository() {
      return {
        async loadStatus() {
          return (await httpsCallable(functions, 'loadMigrationAuthorityStatus')({})).data;
        },
        async importRehearsalBundle(input: MigrationRehearsalImport) {
          if (
            input.bundle.tenantId !== 'tenant-kop' ||
            input.bundle.siteId !== 'site-eumseong' ||
            input.bundle.asanReadCount !== 0 ||
            input.bundle.targetWriteAuthorized !== false ||
            input.bundle.targetWriteCount !== 0 ||
            input.bundle.cutoverAllowed !== false ||
            input.sourceAllowlistHash !== input.bundle.sourceAllowlistHash
          ) {
            throw new Error('ZERO_WRITE_EUMSEONG_REHEARSAL_BUNDLE_REQUIRED');
          }
          return (
            await httpsCallable<MigrationRehearsalImport, Readonly<Record<string, unknown>>>(
              functions,
              'importMigrationRehearsalBundle',
            )(input)
          ).data;
        },
      };
    },
    async loadInspectionReadSnapshot(pageSize = 250) {
      if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 500) {
        throw new Error('Inspection projection page size must be between 1 and 500');
      }
      const read = async (collectionName: string, siteId: 'site-eumseong' | 'eumseong') => {
        type Cursor = QueryDocumentSnapshot<DocumentData, DocumentData>;
        return collectCursorPages<Record<string, unknown>, Cursor>(async (cursor) => {
          const result = await getDocs(
            query(
              collection(firestore, 'tenants', 'kop', collectionName),
              where('scope.siteId', '==', siteId),
              orderBy('date', 'desc'),
              ...(cursor ? [startAfter(cursor)] : []),
              limit(pageSize),
            ),
          );
          return {
            records: result.docs.map((item) => ({ id: item.id, ...item.data() })),
            nextCursor: result.docs.at(-1) ?? null,
            exhausted: result.size < pageSize,
          };
        });
      };
      const [rowResult, psmResult] = await Promise.all([
        read('inspectionReadItems', 'site-eumseong'),
        // PSM remains a separate legacy migration candidate boundary in this F02 wave.
        read('psmLedgerReadItems', 'eumseong'),
      ]);
      return {
        rows: rowResult.records,
        psm: psmResult.records,
        complete: rowResult.cursorExhausted && psmResult.cursorExhausted,
        source: 'server-read' as const,
        limitWarnings: [],
        completeness: {
          schemaVersion: 1,
          strategy: 'cursor-exhaustion' as const,
          scope: { tenantId: 'kop', siteId: 'site-eumseong' },
          legacyMigrationBoundary: 'candidate-only-not-projected' as const,
          collections: {
            inspectionReadItems: {
              records: rowResult.records.length,
              pagesRead: rowResult.pagesRead,
              cursorExhausted: rowResult.cursorExhausted,
            },
            psmLedgerReadItems: {
              records: psmResult.records.length,
              pagesRead: psmResult.pagesRead,
              cursorExhausted: psmResult.cursorExhausted,
            },
          },
        },
      };
    },

    async execute(command: OperationsCommand & Readonly<{ siteId?: string }>) {
      const seed = crypto.randomUUID();
      const callable = httpsCallable(functions, 'executeOperationsResourceCommand');
      const selectedSiteId = command.siteId ?? 'site-eumseong';
      return (
        await callable({
          schemaVersion: 1,
          tenantId: 'kop',
          siteId: selectedSiteId,
          commandId: `operations-${seed}`,
          idempotencyKey: `operations-${seed}`,
          ...command,
        })
      ).data;
    },
    async loadHeatIndexRecords(pageSize = 100, siteId = 'site-eumseong') {
      if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 250)
        throw new Error('HEAT_PAGE_SIZE_INVALID');
      const records: Array<Record<string, unknown>> = [];
      const identifiers = new Set<string>();
      let cursor: string | null = null;
      let expectedCount: number | null = null;
      let expectedRoot: string | null = null;
      let pagesRead = 0;
      for (;;) {
        const response = await httpsCallable(
          functions,
          'listHeatIndexRecords',
        )({
          tenantId: 'kop',
          siteId,
          cursor,
          pageSize,
        });
        const page = response.data as Record<string, unknown>;
        const scope = page.scope as Record<string, unknown> | undefined;
        const reconciliation = page.reconciliation as Record<string, unknown> | undefined;
        const pageRecords = Array.isArray(page.records)
          ? (page.records as Array<Record<string, unknown>>)
          : [];
        if (
          scope?.tenantId !== 'kop' ||
          scope.siteId !== siteId ||
          reconciliation?.strategy !== 'stable-root-and-cursor-exhaustion' ||
          !Number.isInteger(reconciliation.expectedCount) ||
          !/^[a-f0-9]{64}$/u.test(String(reconciliation.expectedRoot ?? '')) ||
          Number(reconciliation.pageCount) !== pageRecords.length ||
          (await sha256(pageRecords)) !== reconciliation.pageRoot
        )
          throw new Error('HEAT_RECONCILIATION_INVALID');
        expectedCount ??= Number(reconciliation.expectedCount);
        expectedRoot ??= String(reconciliation.expectedRoot);
        if (
          expectedCount !== Number(reconciliation.expectedCount) ||
          expectedRoot !== String(reconciliation.expectedRoot)
        )
          throw new Error('HEAT_RECONCILIATION_DRIFT');
        for (const record of pageRecords) {
          const aggregateId = String(record.aggregateId ?? '');
          if (!aggregateId || identifiers.has(aggregateId))
            throw new Error('HEAT_RECONCILIATION_DUPLICATE');
          identifiers.add(aggregateId);
          records.push(record);
        }
        pagesRead += 1;
        if (page.exhausted === true) break;
        if (typeof page.nextCursor !== 'string' || !page.nextCursor || page.nextCursor === cursor)
          throw new Error('HEAT_CURSOR_DID_NOT_ADVANCE');
        cursor = page.nextCursor;
      }
      const loadedRoot = await sha256(records);
      if (records.length !== expectedCount || loadedRoot !== expectedRoot)
        throw new Error('HEAT_RECONCILIATION_INCOMPLETE');
      return {
        records,
        complete: true as const,
        reconciliation: {
          expectedCount,
          loadedCount: records.length,
          expectedRoot,
          loadedRoot,
          pagesRead,
          cursorExhausted: true as const,
        },
      };
    },
    async get(domain: OperationsCommand['domain'], aggregateId: string) {
      if (domain !== 'heat-index') throw new Error('UNSUPPORTED_OPERATIONS_DOMAIN');
      const collection = 'heatIndexRecords';
      const snapshot = await getDoc(doc(firestore, 'tenants', 'kop', collection, aggregateId));
      return snapshot.exists() ? snapshot.data() : null;
    },
  };
}

type IdentitySessionUser = Pick<User, 'uid' | 'email' | 'displayName'>;

/**
 * The single routing decision for a signed-in account.
 *
 * "Set up" is not the server's `complete` alone. `complete` is an internal
 * milestone the server has to reach before it will accept a personal signature
 * at all - `personal-signature-commit.ts` gates `createIntent` and `commit` on
 * `isActiveEumseongOwner`, which requires
 * `canonicalOnboardingHeads/{uid}.status === 'complete'`. So the account is only
 * usable once both are true: onboarding is complete AND an active personal
 * signature is registered, because the signature is what puts a person into an
 * approval line.
 *
 * Both halves are read from the server. Neither is cached or inferred.
 */
async function loadIdentityState(
  auth: ReturnType<typeof getAuth>,
  functions: ReturnType<typeof getFunctions>,
  user: IdentitySessionUser,
  onEntitled: () => void,
) {
  const load = httpsCallable(functions, 'loadIdentitySession', {
    // The production callable consumes App Check tokens for replay protection.
    // Requesting a limited-use token here prevents a prior session probe in this
    // browser (or another mounted observer) from turning the next login into
    // FAILED_PRECONDITION.
    limitedUseAppCheckTokens: true,
  });
  const response = await retryIdentitySessionRead(
    () => load({}),
    () => getToken(identityAppCheck(functions.app.name), true),
  );
  const session = response.data as Record<string, unknown>;
  const sessionFailure = identitySessionFailure(session.state);
  if (sessionFailure) return { state: 'session-unavailable' as const, code: sessionFailure };
  if (session.state === 'registration-required')
    return {
      state: 'registration-required' as const,
      email: user.email ?? '',
      preRegistrationIntentId: String(session.preRegistrationIntentId ?? ''),
    };
  if (session.state !== 'active')
    return {
      state: 'pending' as const,
      email: user.email ?? '',
      requestedAt: String(session.requestedAt ?? ''),
      requestedScope: String(session.requestedScope ?? '음성공장 접근'),
      accessRequest: session.accessRequest ?? null,
    };
  /* The server has said this account is an active member. That is the earliest point at which
   * the workspace's own reads are known to be reads this account may make, and the two setup
   * probes below are each a further cold callable, so the signal is raised here rather than
   * after them. Nothing about the account's scopes is settled yet, so a listener may only start
   * work that carries no scope. */
  onEntitled();
  const authority = canonicalIdentitySessionAuthority(session);
  const identity = (session.identity ?? {}) as Record<string, unknown>;
  const base = {
    identity: {
      id: user.uid,
      displayName: user.displayName ?? String(identity.displayName ?? user.uid),
      email: user.email ?? String(identity.email ?? ''),
    },
    scopes: authority.scopes,
    activeScopeId: authority.defaultActiveScopeId,
  };
  if (session.workspaceReady === true && !requiresHeadquartersOnboarding(authority.scopes))
    return { state: 'authenticated' as const, ...base };
  const probe = await createCanonicalOnboardingPort(functions).probeState();
  if (!probe.ok)
    return {
      state: 'setup-unavailable' as const,
      ...base,
      stage: 'onboarding' as const,
      code: probe.code,
    };
  const onboarding = probe.value;
  const gate = (onboarding.state ?? {}) as { status?: unknown };
  if (gate.status !== 'complete')
    return { state: 'onboarding-required' as const, ...base, onboarding, signature: null };
  /* Only now is the signature even askable: the server refuses one until its own
   * `complete` is recorded, so this is the second half of one procedure. */
  const required = personalSignatureRequired(onboarding);
  const signatureSiteId = issuedPersonalSignatureSiteId(onboarding);
  const signature = await createPersonalSignaturePort(auth, functions, async () => {
    if (!signatureSiteId) throw setupFailure('SIGNATURE_SCOPE_REQUIRED');
    return signatureSiteId;
  }).probeState(required);
  if (!signature.ok)
    return {
      state: 'setup-unavailable' as const,
      ...base,
      stage: 'signature' as const,
      code: signature.code,
    };
  if (signature.value.required && !signature.value.registered)
    return {
      state: 'onboarding-required' as const,
      ...base,
      onboarding,
      signature: signature.value,
    };
  return { state: 'authenticated' as const, ...base };
}

export type CanonicalOnboardingDraftEnvelope = {
  expectedVersion: number;
  idempotencyKey: string;
  draft: Readonly<Record<string, unknown>>;
};

/**
 * Canonical onboarding transport. Every value the screen renders comes from
 * these App Check protected callables; nothing is derived or cached locally.
 */
function isOnboardingProjection(value: unknown): value is Readonly<Record<string, unknown>> {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const state = candidate.state as Record<string, unknown> | undefined;
  return (
    candidate.source === 'server-read' &&
    typeof state === 'object' &&
    state !== null &&
    typeof state.version === 'number' &&
    typeof candidate.approved === 'object' &&
    candidate.approved !== null &&
    typeof candidate.options === 'object' &&
    candidate.options !== null
  );
}

function withoutRecoveryOnboardingScope(value: Readonly<Record<string, unknown>>) {
  const approved = value.approved as Record<string, unknown>;
  const scopes = Array.isArray(approved.scopes)
    ? approved.scopes.filter((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        return String((entry as Record<string, unknown>).persona ?? '') !== 'system-admin';
      })
    : [];
  if (scopes.length === 0) throw setupFailure('ONBOARDING_PROJECTION_INVALID');
  const currentDefault = String(approved.defaultScopeId ?? '');
  const visibleDefault = scopes.some(
    (entry) => String((entry as Record<string, unknown>).scopeId ?? '') === currentDefault,
  )
    ? currentDefault
    : String((scopes[0] as Record<string, unknown>).scopeId ?? '');
  return { ...value, approved: { ...approved, scopes, defaultScopeId: visibleDefault } };
}

function createCanonicalOnboardingPort(functions: ReturnType<typeof getFunctions>) {
  const call = async (name: string, payload: Readonly<Record<string, unknown>>) =>
    (await httpsCallable(functions, name, { limitedUseAppCheckTokens: true })(payload)).data;
  const projection = async (name: string, payload: Readonly<Record<string, unknown>>) => {
    const data = await call(name, payload);
    if (!isOnboardingProjection(data))
      throw Object.assign(new Error('ONBOARDING_PROJECTION_INVALID'), {
        code: 'ONBOARDING_PROJECTION_INVALID',
        details: { code: 'ONBOARDING_PROJECTION_INVALID' },
      });
    return withoutRecoveryOnboardingScope(data);
  };
  return {
    loadOnboardingOptions: (): Promise<unknown> => projection('loadCanonicalOnboardingOptions', {}),
    saveOnboardingDraft: (input: CanonicalOnboardingDraftEnvelope): Promise<unknown> =>
      projection('saveCanonicalOnboardingDraft', {
        expectedVersion: input.expectedVersion,
        idempotencyKey: input.idempotencyKey,
        draft: input.draft,
      }),
    /** Fails closed: an absent or non-complete receipt is never reported as done. */
    async completeOnboarding(input: { expectedVersion: number; idempotencyKey: string }) {
      const data = await call('completeCanonicalOnboarding', {
        expectedVersion: input.expectedVersion,
        idempotencyKey: input.idempotencyKey,
      });
      const receipt = data as Record<string, unknown> | null;
      if (
        typeof receipt !== 'object' ||
        receipt === null ||
        typeof receipt.receiptId !== 'string' ||
        receipt.status !== 'complete'
      )
        throw Object.assign(new Error('ONBOARDING_RECEIPT_MISSING'), {
          code: 'ONBOARDING_RECEIPT_MISSING',
          details: { code: 'ONBOARDING_RECEIPT_MISSING' },
        });
    },
    /**
     * Routing probe. It no longer fails open.
     *
     * The onboarding callables ship in their own deployment codebase
     * (`firebase.onboarding-production-wave.json`, codebase
     * `onboarding-production-wave`), so a failure here is ambiguous: App Check,
     * a cold start, a region outage, a codebase not yet rolled out, or a genuine
     * "onboarding is required".
     *
     * The previous behaviour resolved that ambiguity by admitting the user to
     * the workspace, which made a mandatory gate impossible: one flaky call and
     * an unfinished account was inside. Failing closed on the first error is the
     * opposite mistake, because most of these failures are transient.
     *
     * So the probe retries with backoff and, only when every attempt has failed,
     * reports the failure to the caller as a routing outcome of its own. The
     * caller renders an explicit blocked state with a retry action; it never
     * silently admits anyone. Authentication and authority failures are not
     * retried, because repeating them cannot change the answer.
     */
    probeState(): Promise<SetupProbe<Readonly<Record<string, unknown>>>> {
      return retryingSetupProbe(async () => {
        const data = await call('loadCanonicalOnboardingState', {});
        if (!isOnboardingProjection(data)) throw setupFailure('ONBOARDING_PROJECTION_INVALID');
        return withoutRecoveryOnboardingScope(data);
      });
    },
  };
}

/**
 * One registered personal signature revision, exactly as `loadMyInfo` projects
 * it. Nothing here is derived on the client.
 */
export type PersonalSignatureRegistration = Readonly<{
  signatureId: string;
  version: number;
  committedAssetId: string;
  contentSha256: string;
  createdAt: string;
}>;

/**
 * Whether this account can be used in an approval line yet.
 *
 * `required` mirrors the server's own precondition for accepting a signature:
 * `assertSignatureOnboarding` in `my-info.ts` demands an approved Eumseong site
 * or contractor scope, so an account the server would never accept a signature
 * from is not held behind one. It is read off the approved scopes the onboarding
 * projection returns, never guessed.
 */
export type PersonalSignatureState = Readonly<{
  required: boolean;
  registered: boolean;
  active: PersonalSignatureRegistration | null;
  myInfoVersion: number;
  source: 'server-read';
}>;

export type SetupProbe<T> =
  Readonly<{ ok: true; value: T }> | Readonly<{ ok: false; code: string }>;

const SETUP_PROBE_ATTEMPTS = 3;
const SETUP_PROBE_BACKOFF_MS = [400, 1_200] as const;
/** Repeating these cannot change the answer, so they end the probe immediately. */
const SETUP_PROBE_TERMINAL = new Set([
  'unauthenticated',
  'functions/unauthenticated',
  'permission-denied',
  'functions/permission-denied',
  'FORBIDDEN',
  'SCOPE_MISMATCH',
  'AUTH_REQUIRED',
]);

function setupDelay(duration: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, duration));
}

function callableFailureCode(error: unknown): string {
  if (typeof error !== 'object' || error === null) return '';
  const details = (error as { details?: unknown }).details;
  if (typeof details === 'object' && details !== null && 'code' in details) {
    const inner = String((details as { code: unknown }).code ?? '');
    if (inner) return inner;
  }
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

function setupFailure(code: string) {
  return Object.assign(new Error(code), { code, details: { code } });
}

/** Retries a routing read with backoff and reports the last failure rather than yielding. */
async function retryingSetupProbe<T>(work: () => Promise<T>): Promise<SetupProbe<T>> {
  let code = 'SETUP_PROBE_UNAVAILABLE';
  for (let attempt = 0; attempt < SETUP_PROBE_ATTEMPTS; attempt += 1) {
    try {
      return { ok: true, value: await work() };
    } catch (error) {
      code = callableFailureCode(error) || code;
      if (SETUP_PROBE_TERMINAL.has(code)) break;
      if (attempt < SETUP_PROBE_ATTEMPTS - 1)
        await setupDelay(SETUP_PROBE_BACKOFF_MS[attempt] ?? 1_200);
    }
  }
  return { ok: false, code };
}

const PUBLIC_SITE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;

/**
 * Finds the one public site that the server issued for the selected canonical
 * onboarding scope. The client never substitutes a site or falls back to
 * Eumseong: an incomplete or internally inconsistent projection leaves the
 * signature transport unavailable until it can be read again.
 */
export function issuedPersonalSignatureSiteId(projection: unknown): string | null {
  const approved = (
    projection as {
      approved?: {
        defaultScopeId?: unknown;
        organization?: { siteId?: unknown };
        scopes?: unknown;
      };
    } | null
  )?.approved;
  if (!approved || typeof approved.defaultScopeId !== 'string' || !Array.isArray(approved.scopes))
    return null;
  const selected = approved.scopes.find(
    (candidate): candidate is { scopeId?: unknown; kind?: unknown; siteId?: unknown } =>
      typeof candidate === 'object' &&
      candidate !== null &&
      (candidate as { scopeId?: unknown }).scopeId === approved.defaultScopeId,
  );
  const siteId = selected?.siteId;
  if (
    (selected?.kind !== 'site' && selected?.kind !== 'contractor') ||
    typeof siteId !== 'string' ||
    !PUBLIC_SITE_ID.test(siteId) ||
    approved.organization?.siteId !== siteId
  )
    return null;
  return siteId;
}

/** A personal signature is required only for an issued site/contractor default scope. */
export function personalSignatureRequired(projection: unknown): boolean {
  return issuedPersonalSignatureSiteId(projection) !== null;
}

async function fileSha256(file: Blob) {
  const bytes = await file.arrayBuffer();
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

function readSignatureState(data: unknown, required: boolean): PersonalSignatureState {
  const value = data as { signatureRevisions?: unknown; version?: unknown } | null;
  if (
    typeof value !== 'object' ||
    value === null ||
    !Array.isArray(value.signatureRevisions) ||
    typeof value.version !== 'number'
  )
    throw setupFailure('MY_INFO_PROJECTION_INVALID');
  const active = value.signatureRevisions
    .map((item) => item as Record<string, unknown>)
    .filter(
      (item) =>
        item.status === 'active' &&
        typeof item.signatureId === 'string' &&
        typeof item.version === 'number' &&
        typeof item.committedAssetId === 'string' &&
        typeof item.contentSha256 === 'string',
    )
    .sort((left, right) => Number(right.version) - Number(left.version))[0];
  return {
    required,
    registered: active !== undefined,
    active: active
      ? {
          signatureId: String(active.signatureId),
          version: Number(active.version),
          committedAssetId: String(active.committedAssetId),
          contentSha256: String(active.contentSha256),
          createdAt: String(active.createdAt ?? ''),
        }
      : null,
    myInfoVersion: value.version,
    source: 'server-read',
  };
}

/**
 * Personal signature transport: the committed-reference path only.
 *
 * The bytes never travel through a callable. `createPersonalSignatureUploadIntent`
 * returns the one object path its manifest allows, the bytes go straight to
 * Storage under that manifest id carrying the exact metadata both the Storage
 * rule and `personal-signature-commit.ts` re-verify, and
 * `commitPersonalSignatureUpload` turns the manifest into an immutable asset only
 * after it has re-hashed the object and seen a clean malware receipt.
 * `registerPersonalSignature` then binds that committed asset id - never a blob,
 * never a data URL.
 */
function createPersonalSignaturePort(
  auth: ReturnType<typeof getAuth>,
  functions: ReturnType<typeof getFunctions>,
  issuedSiteId: () => Promise<string>,
) {
  const call = async (name: string, payload: Readonly<Record<string, unknown>>) =>
    (await httpsCallable(functions, name, { limitedUseAppCheckTokens: true })(payload)).data;
  const requireUser = () => {
    const user = auth.currentUser;
    if (!user) throw setupFailure('AUTH_REQUIRED');
    return user;
  };
  const requireSite = async () => {
    const siteId = await issuedSiteId();
    if (!PUBLIC_SITE_ID.test(siteId)) throw setupFailure('SIGNATURE_SCOPE_REQUIRED');
    return siteId;
  };
  const read = async (required: boolean): Promise<PersonalSignatureState> => {
    if (!required)
      return {
        required: false,
        registered: false,
        active: null,
        myInfoVersion: 0,
        source: 'server-read',
      };
    const user = requireUser();
    const siteId = await requireSite();
    return readSignatureState(
      await call('loadMyInfo', { siteId, targetIdentityId: user.uid }),
      true,
    );
  };
  return {
    read,
    probeState(required: boolean) {
      return retryingSetupProbe(() => read(required));
    },
    /** Uploads the bytes and returns the committed asset reference. */
    async upload(file: File, dimensions: Readonly<{ width: number; height: number }>) {
      const user = requireUser();
      const siteId = await requireSite();
      if (file.type !== 'image/webp') throw setupFailure('SIGNATURE_FORMAT_INVALID');
      if (!auth.app.options.storageBucket) throw setupFailure('SIGNATURE_STORAGE_UNCONFIGURED');
      const sha256 = await fileSha256(file);
      const seed = crypto.randomUUID();
      const manifestId = `signature-manifest-${seed}`;
      const assetId = `signature-asset-${seed}`;
      const intent = (await call('createPersonalSignatureUploadIntent', {
        tenantId: 'kop',
        siteId,
        manifestId,
        assetId,
        mediaType: 'image/webp',
        byteSize: file.size,
        width: dimensions.width,
        height: dimensions.height,
        sha256,
      })) as { objectPath?: unknown };
      const objectPath = String(intent?.objectPath ?? '');
      if (!objectPath) throw setupFailure('SIGNATURE_INTENT_INVALID');
      await uploadBytes(storageRef(getStorage(auth.app), objectPath), file, {
        contentType: 'image/webp',
        customMetadata: {
          tenantId: 'kop',
          siteId,
          ownerUid: user.uid,
          manifestId,
          assetId,
          purpose: 'personal-signature',
          mediaType: 'image/webp',
          byteSize: String(file.size),
          width: String(dimensions.width),
          height: String(dimensions.height),
          sha256,
        },
      });
      /* The commit is expected to succeed on the first attempt. A signature is drawn
       * in the app, so the commit only re-reads the object and re-validates the image;
       * it no longer waits on an external scan. The few retries are for transient
       * transport failures, not for a receipt that may never arrive. */
      let status = '';
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const committed = (await call('commitPersonalSignatureUpload', {
            tenantId: 'kop',
            siteId,
            manifestId,
          })) as { status?: unknown };
          status = String(committed?.status ?? '');
          break;
        } catch (error) {
          if (attempt === 2) throw error;
          await setupDelay(1_500);
        }
      }
      if (status !== 'committed') throw setupFailure('SIGNATURE_COMMIT_FAILED');
      return { assetId, contentSha256: sha256 };
    },
    async register(input: {
      expectedVersion: number;
      committedAssetId: string;
      contentSha256: string;
    }): Promise<PersonalSignatureState> {
      const siteId = await requireSite();
      await call('registerPersonalSignature', {
        siteId,
        expectedVersion: input.expectedVersion,
        signature: {
          signatureId: 'personal-signature',
          committedAssetId: input.committedAssetId,
          contentSha256: input.contentSha256,
          mediaType: 'image/webp',
          explicitUseOnly: true,
        },
      });
      return read(true);
    },
  };
}

export function identityAuthMethods(
  auth: ReturnType<typeof getAuth>,
  functions: ReturnType<typeof getFunctions>,
) {
  const onboarding = createCanonicalOnboardingPort(functions);
  let lastIssuedSignatureSiteId: string | null = null;
  const rememberIssuedSignatureSite = (projection: unknown) => {
    lastIssuedSignatureSiteId = issuedPersonalSignatureSiteId(projection);
    return projection;
  };
  const requireIssuedSignatureSite = async () => {
    if (lastIssuedSignatureSiteId) return lastIssuedSignatureSiteId;
    rememberIssuedSignatureSite(await onboarding.loadOnboardingOptions());
    const siteId = lastIssuedSignatureSiteId;
    if (!siteId) throw setupFailure('SIGNATURE_SCOPE_REQUIRED');
    return siteId;
  };
  const signatures = createPersonalSignaturePort(auth, functions, requireIssuedSignatureSite);
  const executeMobilePasskey = httpsCallable<Readonly<Record<string, unknown>>, unknown>(
    functions,
    'executeMobilePasskey',
  );
  const callMyInfo = async <T>(name: string, payload: Readonly<Record<string, unknown>>) => {
    const user = auth.currentUser;
    if (!user) throw new Error('AUTHENTICATION_REQUIRED');
    const siteId = await requireIssuedSignatureSite();
    const result = await httpsCallable(functions, name, { limitedUseAppCheckTokens: true })({
      siteId,
      ...payload,
    });
    return result.data as T;
  };
  let registrationDeliveryInFlight = false;
  let activeState: Awaited<ReturnType<typeof loadIdentityState>> | null = null;
  let activeListener: (state: unknown) => void = () => undefined;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let sessionStartedAt: number | null = null;
  let sessionLoadUid: string | null = null;
  let sessionLoadInFlight: Promise<void> | null = null;
  let sessionReadGeneration = 0;
  let passwordSignInPending = false;
  let resumeObservedIdentity: (() => void) | null = null;
  let freshAuthenticationUid: string | null = null;
  const entitlementListeners = new Set<(identityId: string) => void>();
  const publish = (
    state: Awaited<ReturnType<typeof loadIdentityState>> | { state: string; code?: string },
  ) => {
    activeState = 'scopes' in state ? state : null;
    activeListener(state);
  };
  /* Asked, never counted down. Every caller below re-reads the clock at the moment it asks, so a
   * laptop that slept through midnight is refused the moment it is looked at again. */
  const endSessionIfDayIsOver = () => {
    if (sessionStartedAt === null || !identitySessionExpired(sessionStartedAt)) return false;
    sessionStartedAt = null;
    if (expiryTimer) clearTimeout(expiryTimer);
    void signOut(auth);
    return true;
  };
  return {
    /**
     * Raised once the server has confirmed an active membership, before the setup probes that
     * follow it. It carries the account id and nothing else: no scope is settled at that point.
     */
    observeEntitlement(listener: (identityId: string) => void) {
      entitlementListeners.add(listener);
      return () => {
        entitlementListeners.delete(listener);
      };
    },
    observe(listener: (state: unknown) => void) {
      activeListener = listener;
      publish({ state: 'loading' });
      /* The timer is only a prompt to ask again; the answer comes from the clock inside
       * `endSessionIfDayIsOver`, so firing late, early or not at all cannot admit a session
       * that is over. */
      const armDayBoundary = () => {
        expiryTimer = replaceIdentityExpiryTimer(
          expiryTimer,
          millisecondsUntilNextLocalDay() + 1_000,
          () => {
            if (!endSessionIfDayIsOver()) armDayBoundary();
          },
        );
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') endSessionIfDayIsOver();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      const receiveUser = (user: User | null) => {
        // Persistence may restore yesterday's user while a fresh password exchange is running.
        // Resume from the current Firebase user once that exchange settles, never the old event.
        if (passwordSignInPending) return;
        if (expiryTimer) clearTimeout(expiryTimer);
        sessionStartedAt = null;
        /* Firebase can deliver an already queued `null` observer callback while its
         * persistence layer has already installed the newly signed-in user. Treating that
         * stale callback as a real sign-out briefly remounts the login screen before the
         * following user callback resumes loading. A real sign-out has no current user. */
        if (!user) {
          if (confirmedSignedOut(user, auth.currentUser)) {
            freshAuthenticationUid = null;
            publish({ state: 'signed-out' });
          }
          return;
        }
        if (freshAuthenticationUid && freshAuthenticationUid !== user.uid)
          freshAuthenticationUid = null;
        if (!registrationDeliveryInFlight) {
          if (sessionLoadInFlight && sessionLoadUid === user.uid) return;
          /* A newly observed identity may arrive after the sign-in promise resolves.
           * Move the boundary to its continuous entry state before the entitlement and
           * onboarding reads begin. A settled session stays visible through its own token
           * refresh, but never through a transition to a different identity. */
          const settledIdentityId =
            activeState && 'identity' in activeState ? activeState.identity.id : null;
          if (settledIdentityId !== user.uid) publish({ state: 'loading' });
          sessionLoadUid = user.uid;
          const readGeneration = ++sessionReadGeneration;
          sessionLoadInFlight = user
            .getIdTokenResult()
            .then(async (token) => {
              if (readGeneration !== sessionReadGeneration || auth.currentUser?.uid !== user.uid) return;
              const startedAt = identitySessionStartedAt(token.claims.auth_time);
              if (identitySessionExpired(startedAt)) return signOut(auth);
              sessionStartedAt = startedAt;
              armDayBoundary();
              const state = await loadIdentityState(auth, functions, user, () => {
                if (readGeneration === sessionReadGeneration && auth.currentUser?.uid === user.uid)
                  for (const entitled of entitlementListeners) entitled(user.uid);
              });
              if (readGeneration !== sessionReadGeneration || auth.currentUser?.uid !== user.uid) return;
              if ('onboarding' in state) rememberIssuedSignatureSite(state.onboarding);
              if (state.state === 'authenticated') {
                const selected = sessionStorage.getItem(`kop-active-scope:${user.uid}`);
                if (state.scopes.some((scope) => scope.id === selected))
                  state.activeScopeId = selected!;
              }
              if (readGeneration !== sessionReadGeneration || auth.currentUser?.uid !== user.uid) return;
              publish(state);
            })
            .catch((error: unknown) => {
              if (readGeneration !== sessionReadGeneration || auth.currentUser?.uid !== user.uid) return;
              const rawCode =
                typeof error === 'object' && error !== null && 'code' in error
                  ? String((error as { code?: unknown }).code ?? '')
                  : '';
              publish({
                state: 'session-unavailable',
                code: rawCode.replace(/^functions\//u, '').toUpperCase() || 'SESSION_READ_FAILED',
              });
            })
            .finally(() => {
              if (readGeneration === sessionReadGeneration && sessionLoadUid === user.uid) {
                sessionLoadUid = null;
                sessionLoadInFlight = null;
              }
            });
        }
      };
      resumeObservedIdentity = () => receiveUser(auth.currentUser);
      const unsubscribe = onIdTokenChanged(auth, receiveUser);
      return () => {
        resumeObservedIdentity = null;
        sessionReadGeneration++;
        unsubscribe();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (expiryTimer) clearTimeout(expiryTimer);
        activeListener = () => undefined;
      };
    },
    async signIn(input: { email: string; password: string; rememberMe?: boolean }) {
      if (passwordSignInPending) throw new Error('AUTH_SIGNIN_IN_PROGRESS');
      passwordSignInPending = true;
      sessionReadGeneration++;
      sessionLoadUid = null;
      sessionLoadInFlight = null;
      sessionStartedAt = null;
      if (expiryTimer) clearTimeout(expiryTimer);
      try {
        if (typeof input.rememberMe === 'boolean')
          await setPersistence(auth, input.rememberMe ? browserLocalPersistence : browserSessionPersistence);
        const result = await signInWithEmailAndPassword(auth, input.email, input.password);
        freshAuthenticationUid = result.user.uid;
        await refreshVerifiedIdentity(result.user);
      } finally {
        passwordSignInPending = false;
        resumeObservedIdentity?.();
      }
    },
    consumeFreshAuthentication(identityId: string) {
      if (!identityId || freshAuthenticationUid !== identityId) return false;
      freshAuthenticationUid = null;
      return true;
    },
    async reauthenticateWithPassword(password: string) {
      const user = auth.currentUser;
      if (!user) throw Object.assign(new Error('AUTH_REAUTH_SESSION_REQUIRED'), { code: 'AUTH_REAUTH_SESSION_REQUIRED' });
      if (!user.email) throw Object.assign(new Error('AUTH_REAUTH_EMAIL_REQUIRED'), { code: 'AUTH_REAUTH_EMAIL_REQUIRED' });
      const identityId = user.uid;
      try {
        await reauthenticateWithCredential(
          user,
          EmailAuthProvider.credential(user.email, password),
        );
      } catch (error) {
        throw identityPasswordReauthenticationError(error);
      }
      if (auth.currentUser?.uid !== identityId)
        throw Object.assign(new Error('AUTH_REAUTH_IDENTITY_CHANGED'), { code: 'AUTH_REAUTH_IDENTITY_CHANGED' });
      await user.getIdToken(true);
    },
    async executeMobilePasskey(input: Readonly<Record<string, unknown>>) {
      const identityId = auth.currentUser?.uid;
      if (!identityId) throw new Error('PASSKEY_AUTH_REQUIRED');
      const result = (await executeMobilePasskey(input)).data;
      if (auth.currentUser?.uid !== identityId) throw new Error('PASSKEY_IDENTITY_CHANGED');
      return result;
    },
    async loadRegistrationOptions(
      email: string,
      siteCandidateId?: string | null,
    ): Promise<IdentityRegistrationOptions> {
      return loadIdentityRegistrationOptions(functions, email, siteCandidateId);
    },
    async loadOnboardingOptions() {
      return rememberIssuedSignatureSite(await onboarding.loadOnboardingOptions());
    },
    async saveOnboardingDraft(input: CanonicalOnboardingDraftEnvelope) {
      return rememberIssuedSignatureSite(await onboarding.saveOnboardingDraft(input));
    },
    completeOnboarding: onboarding.completeOnboarding,
    loadPersonalSignature: (required: boolean) => signatures.read(required),
    uploadPersonalSignature: (
      file: File,
      dimensions: Readonly<{ width: number; height: number }>,
    ) => signatures.upload(file, dimensions),
    registerPersonalSignature: (input: {
      expectedVersion: number;
      committedAssetId: string;
      contentSha256: string;
    }) => signatures.register(input),
    loadMyInfo: () =>
      callMyInfo('loadMyInfo', {
        targetIdentityId: auth.currentUser?.uid ?? '',
      }),
    updateMyInfo: (input: { expectedVersion: number; patch: Readonly<Record<string, unknown>> }) =>
      callMyInfo('updateMyInfo', input),
    revokePersonalSignature: (input: {
      expectedVersion: number;
      signatureId: string;
      signatureVersion: number;
    }) => callMyInfo('revokePersonalSignature', input),
    async signUp(input: IdentityRegistrationInput): Promise<IdentityRegistrationReceipt> {
      registrationDeliveryInFlight = true;
      try {
        const result = await createUserWithEmailAndPassword(auth, input.email, input.password);
        try {
          const receipt = await createRegistrationIntent(result.user, functions, input);
          await sendEmailVerification(result.user, {
            url: 'https://safety.kop.co.kr/?registration=complete',
            handleCodeInApp: false,
          });
          await signOut(auth);
          return receipt;
        } catch (error) {
          await result.user.delete().catch(() => undefined);
          throw error;
        }
      } finally {
        registrationDeliveryInFlight = false;
      }
    },
    consumeRegistrationInvite: consumeIdentityRegistrationInvite,
    async completeRegistration(input: IdentityRegistrationCompletionInput) {
      return settleIdentityRegistration(
        () => completeIdentityRegistration(functions, input),
        () =>
          auth.currentUser
            ? updateProfile(auth.currentUser, { displayName: input.displayName })
            : Promise.resolve(),
        () => auth.currentUser?.getIdToken(true) ?? Promise.resolve(),
      );
    },
    async signOut() {
      sessionReadGeneration++;
      freshAuthenticationUid = null;
      await signOut(auth);
    },
    async selectScope(scopeId: string) {
      if (activeState?.state !== 'authenticated') return;
      if (!activeState.scopes.some((scope) => scope.id === scopeId)) return;
      activeState = { ...activeState, activeScopeId: scopeId };
      sessionStorage.setItem(`kop-active-scope:${activeState.identity.id}`, scopeId);
      activeListener(activeState);
    },
    async refreshApproval() {
      if (auth.currentUser) await auth.currentUser.getIdToken(true);
    },
  };
}

export function createIdentityFirebaseEmulatorAuthAdapter(config: OperationsEmulatorConfig) {
  if (!developmentBuild) throw new Error('IDENTITY_AUTH_EMULATOR_ONLY');
  const adapter = createOperationsFirebaseEmulatorAdapter(config);
  const app = getApps().find((candidate) => candidate.name === 'operations-resource-emulator')!;
  const auth = getAuth(app);
  auth.languageCode = 'ko';
  const authHost = config.auth?.host ?? config.firestore.host;
  if (!loopback(authHost)) throw new Error('Identity Auth adapter accepts loopback emulators only');
  connectAuthEmulator(auth, `http://${authHost}:${config.auth?.port ?? 9099}`, {
    disableWarnings: true,
  });
  const functions = getFunctions(app, 'asia-northeast3');
  return {
    ...identityAuthMethods(auth, functions),
    operations: adapter,
  };
}

const appCheckInitialized = new Set<string>();
const appChecks = new Map<string, ReturnType<typeof initializeAppCheck>>();

function identityAppCheck(name: string) {
  const appCheck = appChecks.get(name);
  if (!appCheck) throw new Error('IDENTITY_APP_CHECK_UNAVAILABLE');
  return appCheck;
}

function identityProductionApp(config: IdentityFirebaseConfig) {
  if (
    [config.projectId, config.apiKey, config.appId, config.authDomain, config.appCheckSiteKey].some(
      (value) => value.trim().length === 0,
    )
  )
    throw new Error('Firebase identity configuration is incomplete');
  if (config.projectId.startsWith('demo-'))
    throw new Error('Production identity adapter does not accept demo projects');
  const name = 'identity-production';
  const app =
    getApps().find((candidate) => candidate.name === name) ??
    initializeApp(
      {
        projectId: config.projectId,
        apiKey: config.apiKey,
        appId: config.appId,
        authDomain: config.authDomain,
        ...(config.storageBucket?.trim() ? { storageBucket: config.storageBucket.trim() } : {}),
      },
      name,
    );
  if (!appCheckInitialized.has(name)) {
    appChecks.set(
      name,
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(config.appCheckSiteKey),
        isTokenAutoRefreshEnabled: true,
      }),
    );
    appCheckInitialized.add(name);
  }
  return app;
}

/**
 * Explicit production identity adapter. It does not create fixture identities:
 * session and access decisions always come from App Check protected callables.
 */
export function createIdentityFirebaseAuthAdapter(config: IdentityFirebaseConfig) {
  const app = identityProductionApp(config);
  const auth = getAuth(app);
  auth.languageCode = 'ko';
  const functions = getFunctions(app, 'asia-northeast3');
  return identityAuthMethods(auth, functions);
}

export function createIdentityFirebaseAdminRepository(config: IdentityFirebaseConfig) {
  return identityAdminCallableRepository(
    getFunctions(identityProductionApp(config), 'asia-northeast3'),
  );
}

export function createIdentityAccessNotificationPort(
  config: IdentityFirebaseConfig,
  siteId?: string,
) {
  if (siteId !== undefined && !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId))
    throw new Error('IDENTITY_NOTIFICATION_SITE_REQUIRED');
  const scope = siteId === undefined ? {} : { siteId };
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async count() {
      return (
        await httpsCallable(
          functions,
          'loadIdentityAccessNotifications',
        )({ ...scope, mode: 'count' })
      ).data;
    },
    async list(cursor: string | null = null) {
      return (
        await httpsCallable(
          functions,
          'loadIdentityAccessNotifications',
        )({ ...scope, mode: 'list', cursor, pageSize: 10 })
      ).data;
    },
    async markRead(notificationId: string) {
      return (
        await httpsCallable(
          functions,
          'markIdentityAccessNotificationRead',
        )({ ...scope, notificationId })
      ).data;
    },
  };
}

export function createAccountSettingsFirebaseRepository(config: AccountSettingsFirebaseConfig) {
  if (config.emulator) {
    return createOperationsFirebaseEmulatorAdapter({
      projectId: config.projectId,
      firestore: config.emulator.firestore,
      functions: config.emulator.functions,
    }).accountSettingsRepository();
  }
  return accountSettingsCallableRepository(
    getFunctions(identityProductionApp(config), 'asia-northeast3'),
  );
}

/** Callable-only production transport bound to one server-issued site authority scope. */
export function createContractorDirectoryFirebasePort(
  config: IdentityFirebaseConfig,
  siteId?: string,
) {
  if (siteId !== undefined && !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId))
    throw new Error('CONTRACTOR_DIRECTORY_SITE_REQUIRED');
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  const loadDirectory = httpsCallable(functions, 'loadContractorDirectory', {
    limitedUseAppCheckTokens: true,
  });
  const execute = httpsCallable(functions, 'executeContractorDirectoryCommand', {
    limitedUseAppCheckTokens: true,
  });
  const consumeClaim = httpsCallable(functions, 'consumeExternalContractorClaim', {
    limitedUseAppCheckTokens: true,
  });
  return {
    async load() {
      if (!siteId) throw new Error('CONTRACTOR_DIRECTORY_SITE_REQUIRED');
      return (await loadDirectory({ tenantId: 'kop', siteId })).data;
    },
    async execute(command: Readonly<Record<string, unknown>>) {
      if (!siteId || command.tenantId !== 'kop' || command.siteId !== siteId)
        throw new Error('CONTRACTOR_DIRECTORY_SCOPE_MISMATCH');
      return (await execute(command)).data;
    },
    async consumeClaim(input: Readonly<{ claimId: string; email: string; token?: string }>) {
      if (!input.token) throw new Error('CONTRACTOR_CLAIM_TOKEN_REQUIRED');
      return (await consumeClaim(input)).data;
    },
  };
}

/** Exact six-callable transport for canonical selected-scope product feedback. */
export function createFeedbackOperationsPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  const call = (name: string, input: unknown) => httpsCallable(functions, name)(input);
  return {
    async setup(selectedScopeId: string) {
      return workspaceOperationalRead(functions, 'feedback.setup', { selectedScopeId });
    },
    async submit(input: Readonly<Record<string, unknown>>) {
      return (await call('submitTextFeedback', input)).data;
    },
    async listOwn(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'feedback.own-list', input);
    },
    async detail(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'feedback.detail', input);
    },
    async listTriage(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'feedback.triage-list', input);
    },
    async executeTriage(input: Readonly<Record<string, unknown>>) {
      return (await call('executeFeedbackTriage', input)).data;
    },
  };
}

/** Isolated management transport; never participates in login or existing feature reads. */
export function createFeatureAccessManagementPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async load() {
      const uid = getAuth(functions.app).currentUser?.uid;
      if (!uid) throw new Error('FEATURE_ACCESS_AUTH_REQUIRED');
      const result = await runKeyedAdmittedCallableRead(
        callableReadKey(uid, 'loadFeatureAccessManagement', {}),
        () => httpsCallable(functions, 'loadFeatureAccessManagement')({}),
      );
      if (getAuth(functions.app).currentUser?.uid !== uid) throw new Error('FEATURE_ACCESS_AUTH_CHANGED');
      return result.data;
    },
    async saveDraft(input: Readonly<Record<string, unknown>>) {
      if (!getAuth(functions.app).currentUser) throw new Error('FEATURE_ACCESS_AUTH_REQUIRED');
      return (
        await httpsCallable(functions, 'saveFeatureAccessDraft', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
    async publish(input: Readonly<Record<string, unknown>>) {
      if (!getAuth(functions.app).currentUser) throw new Error('FEATURE_ACCESS_AUTH_REQUIRED');
      return (
        await httpsCallable(functions, 'publishFeatureAccessPolicy', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
    async loadAccountRoles() {
      if (!getAuth(functions.app).currentUser) throw new Error('FEATURE_ACCESS_AUTH_REQUIRED');
      return (await httpsCallable(functions, 'loadAccountAdditionalRoles')({})).data;
    },
    async saveAccountRoles(input: Readonly<Record<string, unknown>>) {
      if (!getAuth(functions.app).currentUser) throw new Error('FEATURE_ACCESS_AUTH_REQUIRED');
      return (
        await httpsCallable(functions, 'saveAccountAdditionalRoles', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

/**
 * Transport for 현장 위험요인 제안: the deployed `hazard-report-operations-production-wave`
 * callables (region `asia-northeast3`, Auth and App Check enforced), reusing the shared
 * authenticated production app so no second Firebase app is created and nothing is constructed at
 * module scope.
 *
 * Separate from `createFeedbackOperationsPort` because the records are separate: a plant hazard is
 * not a software defect, it is gated on `hazard-report:*` rather than `feedback:*`, and it lives
 * in `tenants/{tenantId}/hazardReports` where every document carries `kind: 'field-hazard'`.
 *
 * `submit`, `listOwn` and `detail` are the three the client repository consumes; `setup`,
 * `listTriage` and `executeTriage` complete the wave for the 안전관리자 triage surface. Every
 * response is returned untouched — the caller validates it, nothing here trusts a shape.
 */
export function createHazardReportOperationsPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  const call = (name: string, input: unknown) => httpsCallable(functions, name)(input);
  return {
    async setup(selectedScopeId: string) {
      return workspaceOperationalRead(functions, 'hazard-report.setup', { selectedScopeId });
    },
    async submit(input: Readonly<Record<string, unknown>>) {
      return (await call('submitFieldHazardReport', input)).data;
    },
    async listOwn(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'hazard-report.own-list', input);
    },
    async detail(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'hazard-report.detail', input);
    },
    async listTriage(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'hazard-report.triage-list', input);
    },
    async executeTriage(input: Readonly<Record<string, unknown>>) {
      return (await call('executeHazardReportTriage', input)).data;
    },
  };
}

/**
 * Transport for the home / 브리핑 board: the deployed `loadSafetyBoardBriefing` callable
 * (safety-board-production-wave), reusing the shared authenticated production app so no
 * second Firebase app is created.
 */
export function createBriefingOperationsPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async loadBriefing() {
      return workspaceOperationalRead(functions, 'safety-board.briefing', {});
    },
  };
}

/**
 * Transport for the per-user work home projection: the deployed `loadWorkHomeProjection`
 * callable (work-home-projection-production-wave, region `asia-northeast3`, App Check
 * enforced). It reuses the shared authenticated production app -- App Check attaches
 * automatically because that app owns App Check init -- so no second Firebase app is created
 * and nothing here is constructed at module scope.
 *
 * The whole projection is returned untouched (`notifications`, `approvals`, `findings`,
 * `dueActions`, `safetyBoard`, `favoriteRouteIds`, `startRouteId`, ...); callers decide which
 * parts they consume. The callable rejects any request key other than `selectedScopeId`, so a
 * null scope is sent as an empty envelope and the server falls back to the caller's own saved
 * default scope.
 */
export function createWorkHomeProjectionPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async loadWorkHome(selectedScopeId: string | null = null) {
      return (
        await httpsCallable(
          functions,
          'loadWorkHomeProjection',
        )(selectedScopeId === null ? {} : { selectedScopeId })
      ).data;
    },
  };
}

/**
 * Read-only canary transport for the consolidated workspace home BFF. The legacy home, board and
 * identity-notification transports remain exported unchanged; callers opt into this port only
 * through their own flag/fallback seam.
 */
export function createWorkspaceHomeReadPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  const load = httpsCallable(functions, 'loadWorkspaceHomeRead', {
    limitedUseAppCheckTokens: true,
  });
  return {
    async load(selectedScopeId: string | null = null) {
      return (await load(selectedScopeId === null ? {} : { selectedScopeId })).data;
    },
  };
}

/**
 * Transport for the 안전보건 게시판: the deployed safety-board-production-wave callables
 * (`listSafetyBoardPosts`, `markSafetyBoardPostRead`, `executeSafetyBoardCommand`; region
 * `asia-northeast3`, App Check and Auth enforced). It reuses the shared authenticated
 * production app -- App Check attaches automatically because that app owns App Check init --
 * so no second Firebase app is created and nothing here is constructed at module scope.
 *
 * `loadSafetyBoardPost` is deliberately not exposed. A list page already carries the whole
 * projection every row and the detail dialog render (title, body, audience, attachments,
 * display state, read state), so re-reading a post the caller already holds would be a second
 * round trip for data it was just given. Leaving the callable out of the port makes that a
 * structural guarantee rather than a convention.
 */
export function createSafetyBoardPort(
  config: Pick<IdentityFirebaseConfig, 'projectId'>,
  siteId: string,
) {
  if (
    config.projectId !== 'kop-safety-51efb' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId)
  )
    throw new Error('BOARD_CONFIGURED_SITE_REQUIRED');
  const currentApp = () => {
    const app = getApps().find((candidate) => candidate.options.projectId === config.projectId);
    if (!app || !getAuth(app).currentUser) throw new Error('BOARD_AUTHENTICATED_APP_REQUIRED');
    return app;
  };
  const currentFunctions = () => getFunctions(currentApp(), 'asia-northeast3');
  const call = async (name: string, input: Readonly<Record<string, unknown>>) =>
    (
      await httpsCallable(currentFunctions(), name, { limitedUseAppCheckTokens: true })({
        ...input,
        siteId,
      })
    ).data;
  return {
    currentIdentityId: () => getAuth(currentApp()).currentUser!.uid,
    async list(limit: number) {
      return workspaceOperationalRead(currentFunctions(), 'safety-board.posts', { limit, siteId });
    },
    async settings() {
      return workspaceOperationalRead(currentFunctions(), 'safety-board.settings', { siteId });
    },
    async executePolicy(input: Readonly<Record<string, unknown>>) {
      return call('executeSafetyBoardPolicyCommand', input);
    },
    async markRead(boardId: string) {
      return call('markSafetyBoardPostRead', { boardId });
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return call('executeSafetyBoardCommand', input);
    },
  };
}

/** Safety-manager-only policy register for the board category master. */
export function createSafetyBoardPolicyRegisterPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async load() {
      return workspaceOperationalRead(functions, 'safety-board.settings', {});
    },
    async upsert(input: {
      expectedVersion: number;
      categories: readonly Readonly<{
        id: string;
        label: string;
        order: number;
        active: boolean;
      }>[];
      maxImages: number;
    }) {
      const seed = crypto.randomUUID();
      return (
        await httpsCallable(
          functions,
          'executeSafetyBoardPolicyCommand',
        )({
          operation: 'policy.upsert',
          commandId: `safety-board-policy-${seed}`,
          idempotencyKey: `safety-board-policy-${seed}`,
          ...input,
        })
      ).data;
    },
  };
}

/**
 * Transport for the 음성공장 점검 분류·점검표 register. Both callables are part of the
 * deployed account-settings production entry and share the signed-in, App Check protected app.
 */
export function createInspectionChecklistRegisterPort(
  config: IdentityFirebaseConfig,
  siteId: string,
) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async load() {
      return (
        await httpsCallable(functions, 'loadInspectionChecklistRegister', {
          limitedUseAppCheckTokens: true,
        })({ siteId })
      ).data;
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(functions, 'executeInspectionChecklistCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

/**
 * Transport for the 구역 / 작업장소 registry: the `loadWorkLocationRegistry` read callable and
 * the `executeSiteOrganizationGovernanceCommand` write callable (both region
 * `asia-northeast3`, App Check enforced). It reuses the shared authenticated production app,
 * so App Check attaches automatically and no second Firebase app is created; nothing here is
 * constructed at module scope.
 */
export function createWorkLocationRegistryPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async loadRegistry(publicSiteId: string) {
      return dedicatedSiteAdministrationRead(functions, 'loadWorkLocationRegistry', {
        siteId: publicSiteId,
      });
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(functions, 'executeSiteOrganizationGovernanceCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

/** Immutable originals and separate user-authored equipment working copies. */
export function createHqOperatingSourcePort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async emergencyDrill(input: Readonly<Record<string, unknown>>) {
      return (await httpsCallable(functions, 'submitAssetCredentialCommand')(input)).data;
    },
    async ledgerDraft(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(
          functions,
          'submitAssetCredentialCommand',
        )({ ...input, kind: 'hq.operating.load-source-draft' })
      ).data;
    },
    async saveLedgerDraft(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(
          functions,
          'submitAssetCredentialCommand',
        )({ ...input, kind: 'hq.operating.save-source-draft' })
      ).data;
    },
    async list(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'hq-operating.originals', input);
    },
  };
}

/** One read of acknowledged HQ saves and factory projections; no feature-ledger fan-out. */
export function createHqDashboardReadPort(config: IdentityFirebaseConfig, expectedUid: string) {
  const app = identityProductionApp(config), functions = getFunctions(app, 'asia-northeast3');
  const current = () => {
    if (!expectedUid || getAuth(app).currentUser?.uid !== expectedUid) throw Error('HQ_DASHBOARD_AUTH_SESSION_CHANGED');
  };
  const read = async (name: string, input: Readonly<Record<string, unknown>>) => {
    await getAuth(app).authStateReady();
    current();
    const result = await runKeyedAdmittedCallableRead(callableReadKey(expectedUid, name, input),
      async () => { current(); return (await httpsCallable(functions, name, { limitedUseAppCheckTokens: false })(input)).data; });
    current();
    return result;
  };
  return {
    async load(input: Readonly<{ year: number; half: 1 | 2; day: string; includeInspection?: boolean }>) {
      return read('loadHqDashboardOverview', input);
    },
    async detail(input: Readonly<{ id: string }>) { return read('loadHqHistoricalReport', input); },
  };
}

/** HQ draft reviews use the shared authenticated app and the server's existing HQ authority. */
export function createHqHalfyearReviewPort(config: IdentityFirebaseConfig) {
  const app = identityProductionApp(config);
  const functions = getFunctions(app, 'asia-northeast3');
  let uid = getAuth(app).currentUser?.uid;
  const ready = async () => {
    await getAuth(app).authStateReady();
    uid ??= getAuth(app).currentUser?.uid;
    current();
  };
  const current = () => {
    if (!uid || getAuth(app).currentUser?.uid !== uid)
      throw new Error('HQ_HALFYEAR_AUTH_SESSION_CHANGED');
  };
  const call = async (name: string, payload: Readonly<Record<string, unknown>>) => {
    await ready();
    current();
    const result = await httpsCallable(functions, name, { limitedUseAppCheckTokens: name !== 'loadHqHalfyearReview' })(payload);
    current();
    return result.data;
  };
  return {
    async loadControl(input: Readonly<{ year: number; half: 1 | 2 }>) {
      const payload = { ...input, kind: 'control' };
      await ready();
      return runKeyedAdmittedCallableRead(callableReadKey(uid!, 'loadHqHalfyearReview', payload),
        () => call('loadHqHalfyearReview', payload));
    },
    async loadAnnual(input: Readonly<{ year: number }>) {
      const payload = { ...input, kind: 'annual-policy' };
      await ready();
      return runKeyedAdmittedCallableRead(callableReadKey(uid!, 'loadHqHalfyearReview', payload),
        () => call('loadHqHalfyearReview', payload));
    },
    async saveAnnual(input: Readonly<Record<string, unknown>>) {
      return call('executeHqHalfyearReview', { ...input, kind: 'annual-policy' });
    },
    async load(input: Readonly<{ year: number; half: 1 | 2 }>) {
      await ready();
      return runKeyedAdmittedCallableRead(
        callableReadKey(uid!, 'loadHqHalfyearReview', input),
        () => call('loadHqHalfyearReview', input),
      );
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return call('executeHqHalfyearReview', input);
    },
  };
}

/** Authenticated callable transport for the selected-site meeting-minutes ledger. */
export function createSafetyProgramOperationsPort(config: IdentityFirebaseConfig) {
  const app = identityProductionApp(config);
  const functions = getFunctions(app, 'asia-northeast3');
  const uid = getAuth(app).currentUser?.uid;
  const current = () => {
    if (!uid || getAuth(app).currentUser?.uid !== uid) throw Error('SAFETY_PROGRAM_IDENTITY_CHANGED');
  };
  return {
    async certificate(action: 'analyze' | 'register', input: Readonly<Record<string,unknown>>) {
      current();
      const name=action==='analyze'?'analyzeSafetyEquipmentCertificate':'registerSafetyEquipmentCertificate';
      const result=(await httpsCallable(functions,name,{limitedUseAppCheckTokens:true,timeout:120000})(input)).data;
      current();return result;
    },
    async evidence(action: 'prepare' | 'commit' | 'open', input: Readonly<Record<string, unknown>>) {
      current();
      const name = { prepare: 'prepareSafetyProgramEvidence', commit: 'commitSafetyProgramEvidence', open: 'openSafetyProgramEvidence' }[action];
      const result = (await httpsCallable(functions, name, { limitedUseAppCheckTokens: true })(input)).data;
      current();
      return result;
    },
    async load(input: Readonly<Record<string, unknown>>) {
      current();
      const result = await runKeyedAdmittedCallableRead(callableReadKey(uid!, 'loadSafetyProgramWorkspace', input),
        async () => (await httpsCallable(functions, 'loadSafetyProgramWorkspace')(input)).data);
      current();
      return result;
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      current();
      const result = (await httpsCallable(functions, 'executeSafetyProgramCommand', { limitedUseAppCheckTokens: true })(input)).data;
      current();
      return result;
    },
  };
}

/** Shared-auth read transport; deliberately exposes no heat record command. */
export function createHeadquartersHeatReadPort(config: IdentityFirebaseConfig, expectedActorUid: string) {
  const app = identityProductionApp(config);
  const functions = getFunctions(app, 'asia-northeast3');
  return {
    async load(siteId: string) {
      if (!siteId || !expectedActorUid) throw Error('HEAT_READ_SCOPE_REQUIRED');
      const current = () => {
        if (getAuth(app).currentUser?.uid !== expectedActorUid) throw Error('HEAT_READ_AUTH_SESSION_CHANGED');
      };
      return runKeyedAdmittedCallableRead(callableReadKey(expectedActorUid, 'listHeatIndexRecords', { siteId }), async () => {
        const records: Array<Record<string, unknown>> = [];
        const identifiers = new Set<string>();
        let cursor: string | null = null;
        let expectedCount: number | null = null;
        let expectedRoot: string | null = null;
        for (let pages = 0; pages < 1000; pages += 1) {
          current();
          const page = (await httpsCallable(functions, 'listHeatIndexRecords', { limitedUseAppCheckTokens: true })({ tenantId: 'kop', siteId, cursor, pageSize: 100 })).data as Record<string, unknown>;
          current();
          const scope = page.scope as Record<string, unknown> | undefined;
          const proof = page.reconciliation as Record<string, unknown> | undefined;
          const rows = Array.isArray(page.records) ? page.records as Array<Record<string, unknown>> : [];
          if (scope?.tenantId !== 'kop' || scope.siteId !== siteId || proof?.strategy !== 'stable-root-and-cursor-exhaustion' ||
            !Number.isInteger(proof.expectedCount) || Number(proof.expectedCount) < 0 ||
            !/^[a-f0-9]{64}$/u.test(String(proof.expectedRoot ?? '')) || Number(proof.pageCount) !== rows.length ||
            await sha256(rows) !== proof.pageRoot) throw Error('HEAT_RECONCILIATION_INVALID');
          expectedCount ??= Number(proof.expectedCount);
          expectedRoot ??= String(proof.expectedRoot);
          if (expectedCount !== Number(proof.expectedCount) || expectedRoot !== proof.expectedRoot) throw Error('HEAT_RECONCILIATION_DRIFT');
          for (const row of rows) {
            const id = String(row.aggregateId ?? '');
            if (!id || identifiers.has(id) || row.siteId !== siteId) throw Error('HEAT_RECONCILIATION_INVALID');
            identifiers.add(id); records.push(row);
          }
          if (page.exhausted === true) {
            if (records.length !== expectedCount || await sha256(records) !== expectedRoot) throw Error('HEAT_RECONCILIATION_INCOMPLETE');
            current();
            return { records, complete: true as const };
          }
          if (typeof page.nextCursor !== 'string' || !page.nextCursor || page.nextCursor === cursor) throw Error('HEAT_CURSOR_DID_NOT_ADVANCE');
          cursor = page.nextCursor;
        }
        throw Error('HEAT_READ_PAGE_BOUND_EXCEEDED');
      });
    },
  };
}

export function createMeetingMinutesOperationsPort(config: IdentityFirebaseConfig, expectedActorUid?: string) {
  const app = identityProductionApp(config);
  const functions = getFunctions(app, 'asia-northeast3');
  return {
    async listSites() {
      const uid = expectedActorUid ?? getAuth(app).currentUser?.uid;
      const current = () => {
        if (!uid || getAuth(app).currentUser?.uid !== uid) throw new Error('HQ_SITES_AUTH_SESSION_CHANGED');
      };
      current();
      return runKeyedAdmittedCallableRead(callableReadKey(uid!, 'listMeetingSites', {}), async () => {
        current();
        const result = (await httpsCallable(functions, 'listMeetingSites', { limitedUseAppCheckTokens: true })({})).data;
        current();
        return result;
      });
    },
    async list(input: Readonly<Record<string, unknown>>) {
      return workspaceOperationalRead(functions, 'meeting.records', input);
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(functions, 'executeContentCommunicationsCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
    async loadSchedule(input: Readonly<{ siteId: string }>) {
      return workspaceOperationalRead(functions, 'meeting.schedule', input);
    },
    async loadCalendar(input: Readonly<{ siteId: string; month: string }>) {
      return (await httpsCallable(functions, 'loadMeetingScheduleWorkspace')({ ...input, view: 'site-calendar' })).data;
    },
    async executeCalendar(input: Readonly<Record<string, unknown>>) {
      return (await httpsCallable(functions, 'executeMeetingScheduleCommand', {
        limitedUseAppCheckTokens: true,
      })({ ...input, operation: 'site-calendar.save' })).data;
    },
    async executeSchedule(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(functions, 'executeMeetingScheduleCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

/** Authenticated callable transport for the Eumseong vehicle/equipment dispatch aggregate. */
export function createEquipmentDispatchOperationsPort(
  config: IdentityFirebaseConfig,
  actorUid?: string,
) {
  const app = identityProductionApp(config);
  const functions = getFunctions(app, 'asia-northeast3');
  const uid = actorUid ?? getAuth(app).currentUser?.uid;
  const current = () => {
    if (!uid || getAuth(app).currentUser?.uid !== uid) throw Error('DISPATCH_IDENTITY_CHANGED');
  };
  return {
    async load(input: Readonly<Record<string, unknown>>) {
      current();
      const result = await workspaceOperationalRead(
        functions,
        'equipment-dispatch.workspace',
        input,
      );
      current();
      return result;
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      current();
      const result = (
        await httpsCallable(functions, 'executeEquipmentDispatchCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
      current();
      return result;
    },
  };
}

/**
 * Transport for 근로자 안전명부 — the ten authenticated callables of
 * `worker-roster-production-wave` (region `asia-northeast3`, Auth and App Check enforced). It
 * reuses the shared authenticated production app, so App Check attaches automatically and no
 * second Firebase app is created; nothing here is constructed at module scope.
 *
 * `loadWorkerRosterWorker` is deliberately not exposed. Every 명부 row already carries its own
 * education, health, eligibility, placement and version in the list projection, so a per-row read
 * would be a second round trip for data the caller was just handed. The only thing that callable
 * adds is the consent-gated contact document, which no list screen shows. Leaving it out makes the
 * one-read-per-open rule structural rather than a convention.
 */
export function createWorkerRosterPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  const call = async (name: string, input: Readonly<Record<string, unknown>>) =>
    (await httpsCallable(functions, name)(input)).data;
  return {
    loadSetup: (input: Readonly<Record<string, unknown>>) =>
      workspaceOperationalRead(functions, 'worker-roster.setup', input),
    listInvitations: (input: Readonly<Record<string, unknown>>) =>
      workspaceOperationalRead(functions, 'worker-roster.invitations', input),
    issueInvitation: (input: Readonly<Record<string, unknown>>) =>
      call('issueWorkerRosterInvitation', input),
    revokeInvitation: (input: Readonly<Record<string, unknown>>) =>
      call('revokeWorkerRosterInvitation', input),
    listRegistrations: (input: Readonly<Record<string, unknown>>) =>
      workspaceOperationalRead(functions, 'worker-roster.registrations', input),
    decideRegistration: (input: Readonly<Record<string, unknown>>) =>
      call('decideWorkerRosterRegistration', input),
    listRoster: (input: Readonly<Record<string, unknown>>) =>
      workspaceOperationalRead(functions, 'worker-roster.list', input),
    placeWorker: (input: Readonly<Record<string, unknown>>) =>
      call('placeWorkerRosterWorker', input),
    recordDeparture: (input: Readonly<Record<string, unknown>>) =>
      call('recordWorkerRosterDeparture', input),
  };
}

/**
 * Transport for the worker's own registration page — the two unauthenticated callables of the same
 * wave. They are a separate port, not two more methods on the one above, because they are the only
 * unauthenticated write surface in the system: keeping them apart means an authenticated screen
 * cannot reach them by accident and the registration page cannot reach the other ten.
 *
 * App Check still applies. Unauthenticated is not unattested, and the shared production app is the
 * thing that holds the App Check token, so this reuses it rather than initialising a second one.
 */
export function createWorkerRegistrationPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async loadForm(input: Readonly<Record<string, unknown>>) {
      return (await httpsCallable(functions, 'loadWorkerRegistrationForm')(input)).data;
    },
    async submit(input: Readonly<Record<string, unknown>>) {
      return (await httpsCallable(functions, 'submitWorkerRegistration')(input)).data;
    },
  };
}

/** Same App Check bootstrap for PC opinion management and the time-limited worker link. */
export function createRiskParticipationPort(config: IdentityFirebaseConfig) {
  if (config.projectId !== 'kop-safety-51efb')
    throw new Error('RISK_PARTICIPATION_PROJECT_REQUIRED');
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async call(input: Readonly<Record<string, unknown>>) {
      const kinds = [
        'risk.participation.create',
        'risk.participation.close',
        'risk.participation.list',
        'risk.participation.view',
        'risk.participation.submit',
        'risk.participation.attach',
      ];
      if (!kinds.includes(String(input.kind)))
        throw new Error('RISK_PARTICIPATION_ACTION_REQUIRED');
      return (
        await httpsCallable(functions, 'submitRiskAssessmentCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

/**
 * Transport for the 조직도: the `loadSiteOrganizationGovernance` read callable and the
 * `executeSiteOrganizationGovernanceCommand` write callable (both region `asia-northeast3`,
 * App Check and Auth enforced). It reuses the shared authenticated production app, so App
 * Check attaches automatically and no second Firebase app is created; nothing here is
 * constructed at module scope.
 *
 * `loadSiteOrganizationGovernanceDetail` is deliberately not exposed. The chart already
 * carries every division, department, team, delegation and 점검구역 ownership -- with its
 * version -- in the single projection, so a per-row detail read would be a second round trip
 * for data the caller was just given. Leaving the callable out of the port makes the
 * one-read-per-open rule structural rather than a convention.
 */
export function createSiteOrganizationGovernancePort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async loadChart(publicSiteId: string) {
      return dedicatedSiteAdministrationRead(functions, 'loadSiteOrganizationGovernance', {
        siteId: publicSiteId,
      });
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(functions, 'executeSiteOrganizationGovernanceCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

export type LegacyKpiProjectionPageRequest = Readonly<{
  tenantId: 'kop';
  siteId: 'site-eumseong';
  year: number;
  pageSize: number;
  cursor: string | null;
  expectedCount?: number;
  expectedRoot?: string;
}>;

/** Callable-only KPI transport. It exposes no direct Firestore read or mutation surface. */
export function createLegacyKpiProjectionPagePort(config: OperationsEmulatorConfig) {
  if (
    !config.projectId.startsWith('demo-') ||
    !loopback(config.firestore.host) ||
    !loopback(config.functions.host)
  )
    throw new Error('KPI_PROJECTION_LOOPBACK_ONLY');
  const app = getApps().find(
    (candidate) =>
      candidate.options.projectId === config.projectId && getAuth(candidate).currentUser,
  );
  if (!app) throw new Error('SHARED_AUTHENTICATED_FIREBASE_APP_REQUIRED');
  const auth = getAuth(app);
  const functions = getFunctions(app, 'asia-northeast3');
  connectFunctionsEmulator(functions, config.functions.host, config.functions.port);
  const callable = httpsCallable(functions, 'loadLegacyKpiProjection');
  return {
    async loadPage(request: LegacyKpiProjectionPageRequest) {
      const actorId = auth.currentUser?.uid;
      if (!actorId) throw new Error('KPI_AUTHENTICATION_REQUIRED');
      return (await callable({ ...request, actorId })).data;
    },
  };
}

/** Callable-only HQ retained-materials transport; no direct Firestore surface is exposed. */

export function createBudgetOpexOperationsPort(config: IdentityFirebaseConfig) {
  const functions = getFunctions(identityProductionApp(config), 'asia-northeast3');
  return {
    async load(input: Readonly<Record<string, unknown>>) {
      return (await httpsCallable(functions, 'loadBudgetOpexWorkspace')(input)).data;
    },
    async execute(input: Readonly<Record<string, unknown>>) {
      return (
        await httpsCallable(functions, 'executeBudgetOpexCommand', {
          limitedUseAppCheckTokens: true,
        })(input)
      ).data;
    },
  };
}

import { createHqMonthlyMeetingsPortForApp } from './hq-monthly-meetings';
export type { HqMonthlyMeetingsPort } from './hq-monthly-meetings';
export function createHqMonthlyMeetingsPort(config: IdentityFirebaseConfig, expectedUid: string) {
  return createHqMonthlyMeetingsPortForApp(identityProductionApp(config), expectedUid);
}

import { createHqEmphasisPortForApp } from './hq-emphasis';
export function createHqEmphasisPort(config: IdentityFirebaseConfig, expectedUid: string) {
  return createHqEmphasisPortForApp(identityProductionApp(config), expectedUid);
}
